import React, { FunctionComponent, useEffect, useRef, useState, useMemo } from 'react'
import { createPortal } from 'react-dom'
import Checkbox from '../../../../components/Checkbox'
import { initialConnectionsFilter, useConnectionsStore } from '../../../../stores/connections/useConnectionsStore'
import { useConnectionsHandlers } from '../../../../services/actions/connections/useConnectionsHandlers'
import { IConnection, ITag } from '../../../../interfaces/models/connection'
import ProfilePhoto from '../../../../components/ProfilePhoto'
import { useAppHandlers } from '../../../../services/actions/app/useAppHandlers'
import { SidebarTypes } from '../../../../interfaces/store/appStore'
import { capitalLetters, formatNumber, isChromeBasedBrowser} from '../../../../utils'
import Button from '../../../../components/Button'
import SelectAllCheckbox from '../../../../components/SelectAllCheckbox'
import Text from '../../../../components/Text'
import PhoneInput from '../../../../components/PhoneInput'
import { default as ColumnComponent } from '../../../../components/Column'
import ConnectionTags from '../../../../fragments/ConnectionTags'
import ApplyRemoveTagForm from '../../../../fragments/forms/ApplyRemoveTagForm'
import moment from 'moment'
import { useAppStore } from '../../../../stores/application/useAppStore'
import ConnectionActionDropdown from '../../../../fragments/dropdowns/ConnectionActionDropdown'
import { useLocation } from 'react-router-dom'
import { useAuthStore } from '../../../../stores/auth/useAuthStore'
import useMediaQuery from '../../../../components/useMediaQuery'
import styled from 'styled-components'
import { styles } from '../../../../styles/themes/style'
import Row from '../../../../components/Row'
import AppTag from '../../../../fragments/AppTag'
import { isLNFirst } from './../../../../utils';
import { getID } from "../../../../utils";
import ConnectionTableEmptyPage from './ConnectionTableEmptyPage'
import ActionText from '../../../../components/ActionText'
import DataGrid, { Column } from 'react-data-grid';
import 'react-data-grid/lib/styles.css';
import LoaderCell from '../../../../components/LoaderCell'
import TooltipWrapper from '../../../../components/TooltipWrapper'
import { EnrichmentType } from './EnrichmentTypeEnum'
import { useWorkspaceHandlers } from '../../../../services/actions/workspace/useWorkspaceHandlers'
import posthog from 'posthog-js';
import momentTimezone from 'moment-timezone';
import LoadingSpinner from '../../../../components/LoadingSpinner'
import SvgFragment from '../../../../fragments/SvgFragment'
import { IEnrichedTransaction } from '../../../../components/modal/modals/extension/CalculateEnrichmentModal'
import { useConnectionsActions } from '../../../../stores/connections/useConnectionsActions'
import Select from '../../../../components/select/Select'
import { EmojiPicker } from '../../../../fragments/Emoji'
import Assistant from '../../../../fragments/assistant/Assistant'
import { autosizeTextarea, normalizeMessageForTextarea } from '../../../../utils'
import Calendar from 'react-calendar'
import { useTagsHandlers } from '../../../../services/actions/tags/useTagsHandlers'
import { useAuthHandlers } from '../../../../services/actions/auth/useAuthHandlers'

interface IWorkspaceUser {
    _id: string;
    email: string;
    firstName: string;
    lastName: string;
    profilePicture?: string;
}

interface IConnectionTableProps {
    selectedTags?: ITag[],
    selectedConnections: IConnection[]
    controlBarHeight?: number;
    selectedConnectionsChange: (connections: IConnection[]) => void;
    selectedAllConnectionsChange: (value: boolean) => void;
    enrichConnection?: (connection: IConnection, enrichmentType: EnrichmentType) => void;
    enrichingConnections?: Array<{connectionId: string, type: EnrichmentType}>;
    enrichingTransactionId?: string;
    enrichedTransaction?: IEnrichedTransaction;
}

type ColumnWidths = {
    [key: string]: number;
};

interface RenderCellProps {
    row: IConnection;
    column?: any
}

type InfoType = {
    version: any;
    isLinkedinFirst: any;
    lastMessagedAt: any;
    autoTag: any;
    isDisconnected: boolean;
    enrichedPrivateEmails?: string;
    enrichedBusinessEmails?: string;
    enrichedPhones?: string;
};

// User Field Components
const UserFieldContainer = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    height: 30px;
    border: 1px solid ${styles.colors.black200};
    border-radius: 6px;
    background-color: ${styles.colors.white};
    overflow: hidden;
    box-sizing: border-box;
    position: relative;
    
    &:hover {
        border-color: ${styles.colors.primary600};
    }
    
    &:focus-within {
        border-color: ${styles.colors.primary600};
        box-shadow: 0 0 0 2px rgba(89, 100, 255, 0.1);
    }
`

const UserFieldInput = styled.input`
    flex: 1;
    height: 100%;
    padding: 0 12px 0 12px;
    border: none;
    font-size: 14px;
    line-height: 20px;
    color: ${styles.colors.black600};
    background-color: transparent;
    outline: none;
    box-sizing: border-box;
    
    &::placeholder {
        color: ${styles.colors.black400};
    }
`

const UserFieldDisplay = styled.div`
    flex: 1;
    height: 100%;
    padding: 0 12px 0 12px;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    line-height: 20px;
    color: ${styles.colors.black600};
    cursor: pointer;
    box-sizing: border-box;
    min-width: 0;
    overflow: hidden;
`

const UserFieldChevron = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 100%;
    flex-shrink: 0;
    cursor: pointer;
    
    svg {
        width: 12px;
        height: 12px;
        
        path {
            fill: ${styles.colors.black300};
        }
    }
    
    &:hover svg path {
        fill: ${styles.colors.primary600};
    }
`


const UserFieldSelectedText = styled.span`
    font-size: 14px;
    color: #2C3E50;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex: 1;
    min-width: 0;

    overflow: hidden;

`

const StyledDataGrid = styled(DataGrid<IConnection>)<{ os?: string; }>`
    height: 100%;
    border: 2px solid ${styles.colors.black200};
    background-color: ${styles.colors.uiBackground};

    [role='row'] {
        background-color: ${styles.colors.white} !important;
    }

    [role='row']:not(:first-of-type):hover {
        background-color: ${styles.colors.black100} !important;
        cursor: pointer;
    }

    .rdg-cell {
        border-right: 1px solid ${styles.colors.black200};
        border-bottom: 1px solid ${styles.colors.black200};
        padding: 0 8px 0 8px;
        display: flex;
        align-items: center;
        box-sizing: border-box;
        overflow: visible;
        width: auto;
    }


  .rdg-header-row {
    .rdg-cell {
      cursor: default;

      &:not(:first-child) {
        cursor: grab;

        &:active {
          cursor: grabbing;
        }
      }
    }
    }

    .rdg-header-row {
        position: sticky;
        top: 0;
        z-index: 3;
    }

    .action-cell-class {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 0;
    }

    .user-info-cell-class {
        overflow: visible;
    }

    .company-header-cell-class {
        cursor: default;
    }

    .truncate-cell {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .mutual-connections-cell {
        overflow: hidden !important;
        isolation: isolate;
    }

    .disabled-state {
        background-color: #E7EDF3;
    }

    .user-info {
        z-index: 2;
    }
`;

const ConnectionTable: FunctionComponent<IConnectionTableProps> = ({selectedConnections, controlBarHeight, selectedConnectionsChange, selectedAllConnectionsChange, enrichConnection, enrichingConnections = [], enrichingTransactionId, enrichedTransaction}) => {
    const [actionDropdownState, setActionDropdownState] = useState<{ 
        connection: IConnection | null, 
        x: number, 
        y: number,
        positioned: boolean,
        triggerRect?: { top: number, bottom: number, left: number, right: number, width: number, height: number }
    }>({ connection: null, x: 0, y: 0, positioned: false })
    const portalRef = useRef<HTMLDivElement | null>(null)

    // State for custom field editing - using ref for fast updates
    const editingCustomFieldRef = useRef<{
        connectionId: string | null,
        fieldKey: string | null,
        value: any,
        originalValue: any,
        fieldType: string | null
    }>({ connectionId: null, fieldKey: null, value: null, originalValue: null, fieldType: null })

    const [editingTrigger, setEditingTrigger] = useState(0) // Just for triggering re-render
    const [datePickerState, setDatePickerState] = useState<{
        show: boolean;
        x: number;
        y: number;
        connectionId: string | null;
        fieldKey: string | null;
    }>({ show: false, x: 0, y: 0, connectionId: null, fieldKey: null })
    const datePickerRef = useRef<HTMLDivElement | null>(null)

    const [richTextState, setRichTextState] = useState<{
        show: boolean;
        x: number;
        y: number;
        connectionId: string | null;
        fieldKey: string | null;
        value: string;
    }>({ show: false, x: 0, y: 0, connectionId: null, fieldKey: null, value: '' })
    const [richTextSparkleLoading, setRichTextSparkleLoading] = useState<boolean>(false)
    const richTextRef = useRef<HTMLDivElement | null>(null)
    const richTextTextareaRef = useRef<HTMLTextAreaElement | null>(null)

    const [userDropdownState, setUserDropdownState] = useState<{
        show: boolean;
        x: number;
        y: number;
        connectionId: string | null;
        fieldKey: string | null;
        searchValue: string;
    }>({ show: false, x: 0, y: 0, connectionId: null, fieldKey: null, searchValue: '' })
    const userDropdownRef = useRef<HTMLDivElement | null>(null)

    const [infoBoxState, setInfoBoxState] = useState<{
        show: boolean;
        x: number;
        y: number;
        positioned: boolean;
        connection: IConnection | null;
        triggerRect?: { top: number, bottom: number, left: number, right: number, width: number, height: number };
        isLastColumn?: boolean;
    }>({ show: false, x: 0, y: 0, positioned: false, connection: null, isLastColumn: false })
    const infoBoxRef = useRef<HTMLDivElement | null>(null)
    const [memberSearch, setMemberSearch] = useState<string>('')
    const infoBoxCloseTimeoutRef = useRef<number | null>(null)
    const isHoveringInfoBoxRef = useRef<boolean>(false)

    const cancelInfoBoxClose = () => {
        if (infoBoxCloseTimeoutRef.current) {
            window.clearTimeout(infoBoxCloseTimeoutRef.current)
            infoBoxCloseTimeoutRef.current = null
        }
    }

    const scheduleInfoBoxClose = (delayMs = 120) => {
        cancelInfoBoxClose()
        infoBoxCloseTimeoutRef.current = window.setTimeout(() => {
            if (isHoveringInfoBoxRef.current) return
            setInfoBoxState(prev => ({ ...prev, show: false }))
        }, delayMs)
    }

    const [userInputValue, setUserInputValue] = useState<string>('')
    const [workspaceUsers, setWorkspaceUsers] = useState<IWorkspaceUser[]>([])
    const { workspaceUsersPublicAllHandler } = useTagsHandlers()

    // Helper functions for custom field editing
    const cancelEditingCustomField = () => {
        editingCustomFieldRef.current = { connectionId: null, fieldKey: null, value: null, originalValue: null, fieldType: null };
        setDatePickerState({ show: false, x: 0, y: 0, connectionId: null, fieldKey: null });
        setRichTextState({ show: false, x: 0, y: 0, connectionId: null, fieldKey: null, value: '' });
        setEditingTrigger(prev => prev + 1); // Trigger re-render
    };

    const cancelRichTextEditing = () => {
        setRichTextState({ show: false, x: 0, y: 0, connectionId: null, fieldKey: null, value: '' });
        editingCustomFieldRef.current = { connectionId: null, fieldKey: null, value: null, originalValue: null, fieldType: null };
    };

    const cancelUserDropdownEditing = () => {
        setUserDropdownState({ show: false, x: 0, y: 0, connectionId: null, fieldKey: null, searchValue: '' });
        setUserInputValue('');
        editingCustomFieldRef.current = { connectionId: null, fieldKey: null, value: null, originalValue: null, fieldType: null };
    };

    const openActionDropdownPortal = (e: React.MouseEvent<HTMLElement>, connection: IConnection) => {
        e.stopPropagation()

        const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
        const gap = 10
        const estimatedMenuWidth = 220
        const estimatedMenuHeight = 300
        const viewportWidth = window.innerWidth
        const viewportHeight = window.innerHeight

        const canOpenRight = rect.right + estimatedMenuWidth <= viewportWidth - gap
        let x = canOpenRight ? rect.right + 5 : rect.left - estimatedMenuWidth

        // Pre-compute vertical direction to avoid visual jump after measuring
        const upwardNudge = 30
        const spaceBelowEstimated = viewportHeight - rect.bottom
        const canOpenDownEstimated = spaceBelowEstimated >= estimatedMenuHeight + gap
        let y = canOpenDownEstimated
            ? rect.bottom - 15
            : Math.max(gap, rect.top - estimatedMenuHeight - gap + upwardNudge)

        setActionDropdownState(prev => {
            if (prev.connection?._id === connection._id) {
                return { connection: null, x: 0, y: 0, positioned: false }
            }
            return { 
                connection, 
                x, 
                y, 
                positioned: false,
                triggerRect: { top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right, width: rect.width, height: rect.height }
            }
        })
    }

    // Position InfoBox on hover
    useEffect(() => {
        if (!infoBoxState.show || !infoBoxState.connection || !infoBoxState.triggerRect) return;

        const computeAndSetPosition = () => {
            const portalEl = infoBoxRef.current
            if (!portalEl || !infoBoxState.triggerRect) return

            const menuRect = portalEl.getBoundingClientRect()
            const viewportWidth = window.innerWidth
            const viewportHeight = window.innerHeight
            const gap = 10

            // Horizontal position
            let desiredX = infoBoxState.triggerRect.left
            if (infoBoxState.isLastColumn) {
                desiredX = infoBoxState.triggerRect.right - menuRect.width
            }
            // Ensure it doesn't overflow viewport
            if (desiredX + menuRect.width > viewportWidth - gap) {
                desiredX = Math.max(gap, viewportWidth - menuRect.width - gap)
            }
            desiredX = Math.max(gap, desiredX)

            // Vertical position - prefer below, but adjust if needed
            let desiredY = infoBoxState.triggerRect.bottom + gap
            const spaceBelow = viewportHeight - infoBoxState.triggerRect.bottom
            const spaceAbove = infoBoxState.triggerRect.top

            // If not enough space below, try above
            if (spaceBelow < menuRect.height + gap && spaceAbove > menuRect.height + gap) {
                desiredY = infoBoxState.triggerRect.top - menuRect.height - gap
            } else if (spaceBelow < menuRect.height + gap) {
                // If not enough space above either, position at bottom of viewport
                desiredY = Math.max(gap, viewportHeight - menuRect.height - gap)
            }

            // Clamp vertically within viewport
            desiredY = Math.min(Math.max(gap, desiredY), viewportHeight - menuRect.height - gap)

            setInfoBoxState(prev => {
                // Only update if position actually changed
                if (prev.x === desiredX && prev.y === desiredY && prev.positioned) {
                    return prev
                }
                return { ...prev, x: desiredX, y: desiredY, positioned: true }
            })
        }

        const rafId = requestAnimationFrame(computeAndSetPosition)

        // Recompute on resize/scroll
        const handleResize = () => computeAndSetPosition()
        const handleScroll = () => computeAndSetPosition()

        window.addEventListener('resize', handleResize)
        window.addEventListener('scroll', handleScroll, true)

        // Recompute on InfoBox size changes
        let ro: ResizeObserver | null = null
        if (infoBoxRef.current && 'ResizeObserver' in window) {
            ro = new ResizeObserver(() => computeAndSetPosition())
            ro.observe(infoBoxRef.current)
        }

        return () => {
            cancelAnimationFrame(rafId)
            window.removeEventListener('resize', handleResize)
            window.removeEventListener('scroll', handleScroll, true)
            if (ro) ro.disconnect()
        }
    }, [infoBoxState.show, infoBoxState.connection, infoBoxState.triggerRect, infoBoxState.isLastColumn])

    useEffect(() => {
        if (infoBoxState.show) setMemberSearch('');
    }, [infoBoxState.show, infoBoxState.connection?._id])

    useEffect(() => {
        return () => {
            // cleanup any pending close timer on unmount
            if (infoBoxCloseTimeoutRef.current) {
                window.clearTimeout(infoBoxCloseTimeoutRef.current)
            }
        }
    }, [])

    // After rendering the portal, measure menu size and adjust position to avoid viewport overflow
    useEffect(() => {
        if (!actionDropdownState.connection) return

        const computeAndSetPosition = () => {
            const portalEl = portalRef.current
            const menuEl = portalEl?.querySelector('.dropdown-menu') as HTMLElement | null
            if (!menuEl || !actionDropdownState.triggerRect) return

            // Neutralize Popper inline positioning so menu anchors to our wrapper
            menuEl.style.transform = 'none'
            menuEl.style.position = 'static'
            menuEl.style.top = 'auto'
            menuEl.style.left = 'auto'
            menuEl.style.right = 'auto'
            menuEl.style.bottom = 'auto'

            const menuRect = menuEl.getBoundingClientRect()
            const viewportWidth = window.innerWidth
            const viewportHeight = window.innerHeight
            const gap = 10
            const upwardNudge = 30

            // Desired horizontal position: prefer right side of trigger
            let openRight = true
            let desiredX = actionDropdownState.triggerRect.right
            if (desiredX + menuRect.width > viewportWidth - gap) {
                desiredX = actionDropdownState.triggerRect.left - menuRect.width
                openRight = false
            }
            if (openRight) desiredX += 5 // nudge 5px to the right when opening on the right side
            desiredX = Math.min(Math.max(gap, desiredX), viewportWidth - menuRect.width - gap)

            // Desired vertical position: prefer below
            const spaceBelow = viewportHeight - actionDropdownState.triggerRect.bottom
            const canOpenDown = spaceBelow >= menuRect.height + gap
            let desiredY = canOpenDown
                ? actionDropdownState.triggerRect.bottom - 15
                : Math.max(gap, actionDropdownState.triggerRect.top - menuRect.height - gap + upwardNudge)

            // Clamp vertically within viewport
            desiredY = Math.min(Math.max(gap, desiredY), viewportHeight - menuRect.height - gap)

            if (desiredX !== actionDropdownState.x || desiredY !== actionDropdownState.y || !actionDropdownState.positioned) {
                setActionDropdownState(prev => ({ ...prev, x: desiredX, y: desiredY, positioned: true }))
            }
        }

        const rafId = requestAnimationFrame(computeAndSetPosition)

        // Recompute on DOM mutations inside portal (Popper toggles, dynamic content)
        const mo = new MutationObserver(() => computeAndSetPosition())
        if (portalRef.current) mo.observe(portalRef.current, { childList: true, subtree: true, attributes: true })

        // Recompute on menu size changes
        let ro: ResizeObserver | null = null
        const menuEl = portalRef.current?.querySelector('.dropdown-menu') as HTMLElement | null
        if (menuEl && 'ResizeObserver' in window) {
            ro = new ResizeObserver(() => computeAndSetPosition())
            ro.observe(menuEl)
        }

        return () => {
            cancelAnimationFrame(rafId)
            mo.disconnect()
            if (ro) ro.disconnect()
        }
    }, [actionDropdownState.connection, actionDropdownState.triggerRect, actionDropdownState.x, actionDropdownState.y, actionDropdownState.positioned])

    const closeActionDropdownPortal = () => setActionDropdownState({ connection: null, x: 0, y: 0, positioned: false })

    useEffect(() => {
        if (!actionDropdownState.connection) return

        const handleOutside = () => closeActionDropdownPortal()
        const handleResizeScroll = () => closeActionDropdownPortal()

        document.addEventListener('click', handleOutside)
        window.addEventListener('scroll', handleResizeScroll, true)
        window.addEventListener('resize', handleResizeScroll)

        return () => {
            document.removeEventListener('click', handleOutside)
            window.removeEventListener('scroll', handleResizeScroll, true)
            window.removeEventListener('resize', handleResizeScroll)
        }
    }, [actionDropdownState.connection])

    // Close date picker on outside click
    useEffect(() => {
        if (!datePickerState.show) return

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement
            if (datePickerRef.current && !datePickerRef.current.contains(target)) {
                cancelEditingCustomField()
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        window.addEventListener('scroll', cancelEditingCustomField, true)
        window.addEventListener('resize', cancelEditingCustomField)

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            window.removeEventListener('scroll', cancelEditingCustomField, true)
            window.removeEventListener('resize', cancelEditingCustomField)
        }
    }, [datePickerState.show])

    // Close rich text editor on outside click
    useEffect(() => {
        if (!richTextState.show) return

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement
            // Don't close if clicking inside rich text editor, emoji picker, or modals
            const isClickInsideRichText = richTextRef.current && richTextRef.current.contains(target)
            const isClickInsideEmojiPicker = target.closest('[data-emoji-mart]') ||
                                            target.closest('em-emoji-picker') ||
                                            target.closest('.emoji-picker-button')
            const isClickInsideModal = target.closest('[role="dialog"]') ||
                                       target.closest('.modal') ||
                                       target.closest('[class*="Modal"]') ||
                                       target.closest('[class*="modal"]')

            if (!isClickInsideRichText && !isClickInsideEmojiPicker && !isClickInsideModal) {
                cancelRichTextEditing()
            }
        }

        const handleScroll = (event: Event) => {
            const target = event.target as HTMLElement
            // Don't close if scrolling inside rich text editor, emoji picker, or assistant dropdown
            const isScrollInsideRichText = richTextRef.current && richTextRef.current.contains(target)
            const isScrollInsideEmojiPicker = target.closest('[data-emoji-mart]') ||
                                            target.closest('em-emoji-picker') ||
                                            target.closest('.emoji-picker-button')
            const isScrollInsideModal = target.closest('[role="dialog"]') ||
                                       target.closest('.modal') ||
                                       target.closest('[class*="Modal"]') ||
                                       target.closest('[class*="modal"]')
            const isScrollInsideAssistantDropdown = target.closest('.ai-assistant-dropdown') ||
                                                   target.closest('[class*="DropdownMenu"]') ||
                                                   target.closest('[class*="dropdown"]')

            if (!isScrollInsideRichText && !isScrollInsideEmojiPicker && !isScrollInsideModal && !isScrollInsideAssistantDropdown) {
                cancelRichTextEditing()
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        window.addEventListener('scroll', handleScroll, true)
        window.addEventListener('resize', cancelRichTextEditing)

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
            window.removeEventListener('scroll', handleScroll, true)
            window.removeEventListener('resize', cancelRichTextEditing)
        }
    }, [richTextState.show])

    // Close select on outside click or menu close
    useEffect(() => {
        const isSelectEditing = editingCustomFieldRef.current.fieldType === 'select' &&
                               editingCustomFieldRef.current.connectionId !== null;

        if (!isSelectEditing) return;

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            // Check if click is outside select component
            if (!target.closest('.custom-field-select') &&
                !target.closest('[class*="menu"]') &&
                !target.closest('[class*="Menu"]')) {
                // Delay to allow selection to complete
                setTimeout(() => {
                    if (editingCustomFieldRef.current.fieldType === 'select') {
                        cancelEditingCustomField();
                    }
                }, 100);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [editingTrigger])

    // Close user dropdown on outside click
    useEffect(() => {
        if (!userDropdownState.show) return;

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as HTMLElement;
            if (userDropdownRef.current && !userDropdownRef.current.contains(target)) {
                cancelUserDropdownEditing();
            }
        };

        const handleScroll = (event: Event) => {
            const target = event.target as HTMLElement;
            // Only close if scroll is not inside the dropdown
            // Check if target or any of its parents is inside the dropdown
            if (userDropdownRef.current) {
                let element = target;
                let isInsideDropdown = false;
                while (element && element !== document.body) {
                    if (userDropdownRef.current.contains(element)) {
                        isInsideDropdown = true;
                        break;
                    }
                    element = element.parentElement as HTMLElement;
                }
                if (!isInsideDropdown) {
                    cancelUserDropdownEditing();
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('scroll', handleScroll, true);
        window.addEventListener('resize', cancelUserDropdownEditing);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener('scroll', handleScroll, true);
            window.removeEventListener('resize', cancelUserDropdownEditing);
        }
    }, [userDropdownState.show])

    // Load workspace users - only once on mount
    useEffect(() => {
        let isMounted = true;

        const loadWorkspaceUsers = async () => {
            try {
                const result = await workspaceUsersPublicAllHandler({
                    condition: 'or',
                    name: '',
                    email: ''
                });
                if (isMounted) {
                    setWorkspaceUsers(result || []);
                }
            } catch (error) {
                console.error('Error loading workspace users:', error);
            }
        };

        loadWorkspaceUsers();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []) // Empty dependency array - only run once
    const tableRef = useRef<HTMLDivElement>(null)
    const location = useLocation()
    const {
        tag,
        note,
        noTags,
        industry,
        birthDateFrom,
        birthDateTo,
        isAutotagsFilterOr,
        autotagsFilter,
        selectedUsersMutual,
        locationSearch,
        jobTitleSearch,
        conversationStatus,
        hasCompanyChanges,
        hasJobChanges,
        connectionIdsFilter,
        connectionIdsFilterMeta,
        searchByLocation: {
            searchByLocation = '',
        } = {},
        searchByJobTitle: {
            searchByJobTitle = '',
        } = {}
    } = location.state || {}

    const isSmall = useMediaQuery("(max-width: 768px)");

    const { store: { connections, connectionsFilter, connectionsParameters, customFieldDefinitions, enrichingConnections: sidebarEnrichingConnections } } = useConnectionsStore()
    const { setConnectionsFilterHandler, setConnectionsParametersHandler, estimateEnrichmentCreditsHandler, enrichConnectionsHandler, getEnrichedTransactionByIdHandler, getEnrichedConnectionsHandler, messageClickHandler, updateCustomFieldValueHandler, generateAiRecommendationsHandler, getConnectionAiRecommendationsHandler } = useConnectionsHandlers()
    const { setConnections, patchConnection } = useConnectionsActions()
    const { openSidebar, closeSidebar, showInfoToast, setCompanyOverviewModal, showErrorToast, showSuccessToast, setCreateCustomFieldModal, hideModal, setChooseTemplateModal, setAddMembersModal } = useAppHandlers()
    const { searchPeopleSduiHandler } = useAuthHandlers()
    const { refetchWorkspace } = useWorkspaceHandlers()

    const { store: { requestLoading, extensionInstalled, extensionVersion }} = useAppStore()
    const { store: { workspace: { showAutoTags }, user, isLoggedToLinkedin, workspace, trial, subscription }} = useAuthStore()

    const restrictScanMutualsTo1M = (process.env.REACT_APP_ENABLE_SCAN_MUTUALS_ONLY_FOR_1M_WORKSPACE ?? 'false').toLowerCase() === 'true';
    const oneMWorkspaceId = process.env.REACT_APP_1M_WORKSPACE_ID;
    const canShowScanMutuals = !restrictScanMutualsTo1M || (!!oneMWorkspaceId && workspace?._id === oneMWorkspaceId);
    const canUseWarmIntroAi =
        !!workspace?.aiChatEnabled ||
        !!trial ||
        (!!workspace?.isBusiness && subscription?.status === 'active');

    const todayKeyAi = momentTimezone.tz(new Date(), 'America/New_York').format('YYYY-MM-DD');
    const dailyMaxAi = Number(process.env.REACT_APP_AI_RECOMMENDATIONS_DAILY_LIMIT ?? 20) || 20;
    const usedTodayAi = (user as any)?.aiRecommendationsAnalytics?.dailyDate === todayKeyAi
        ? (Number((user as any)?.aiRecommendationsAnalytics?.dailyCount ?? 0) || 0)
        : 0;
    const remainingTodayAi = Math.max(0, dailyMaxAi - usedTodayAi);
    const dailyLimitReachedAi = remainingTodayAi <= 0;
    const dailyLimitReachedMessage = `You\u2019ve reached your daily limit of ${dailyMaxAi} warm intros.`;

    const workspaceIsStarter = !!workspace?.isStarter;
    const currentUserId = user?._id;

    // Poll until the backend AI generation completes (backend generate is fire-and-forget).
    const pollUntilDone = async (connectionId: string) => {
        const maxAttempts = 24; // 24 × 5 s = 2 min max
        const delayMs = 5000;
        for (let i = 0; i < maxAttempts; i++) {
            await new Promise((res) => setTimeout(res, delayMs));
            try {
                const latest: any = await getConnectionAiRecommendationsHandler(connectionId);
                // Only stop polling on terminal states. Some backends may briefly report "idle"
                // while embeddings/generation are still warming up; treating that as terminal
                // would freeze the UI at 0 results.
                if (latest?.status && (latest.status === 'ready' || latest.status === 'error')) {
                    const matches = latest?.linkedinAiMutualProfilesCount ?? (Array.isArray(latest?.linkedinAiMutualProfiles) ? latest.linkedinAiMutualProfiles.length : 0);
                    patchConnection(connectionId, {
                        aiRecommendationsStatus: latest.status as any,
                        linkedinAiMutualProfilesCount: latest.linkedinAiMutualProfilesCount ?? (Array.isArray(latest.linkedinAiMutualProfiles) ? latest.linkedinAiMutualProfiles.length : 0)
                    } as any);
                    if (latest.status === 'ready') {
                        posthog.capture('find mutuals completed', {
                            source: 'connections_table_row',
                            bulk: false,
                            selectedCount: 1,
                            connectionId,
                            matchesFound: matches,
                        });
                    }
                    return;
                }
            } catch {
                // retry on transient errors
            }
        }
        patchConnection(connectionId, { aiRecommendationsStatus: 'error' as any });
    };

    const scanMutualsForRow = async (connection: IConnection) => {
        if (!connection?._id) return;
        if (dailyLimitReachedAi) {
            showInfoToast({ message: dailyLimitReachedMessage });
            return;
        }

        // Optimistic: show "Analyzing..." immediately — also reset count so sidebar doesn't
        // instantly treat old results as "ready" (some UI derives ready from count > 0).
        patchConnection(connection._id, {
            aiRecommendationsStatus: 'generating' as any,
            linkedinAiMutualProfilesCount: 0
        } as any);

        // Close any existing sidebar for this type first so we don't stack duplicates
        // (e.g. clicking "Try again" while the error sidebar is still open).
        closeSidebar(SidebarTypes.CONNECTION_NETWORK);
        setTimeout(() => {
            openSidebar(SidebarTypes.CONNECTION_NETWORK, {
                connection: { ...connection, aiRecommendationsStatus: 'generating', linkedinAiMutualProfilesCount: 0 } as IConnection
            });
        }, 0);
        showInfoToast({ message: 'AI is analyzing mutual connections. Results will appear shortly.' });

        try {
            if (extensionInstalled && isLoggedToLinkedin) {
                try {
                    await searchPeopleSduiHandler(connection.profileId ?? '', connection.publicIdentifier ?? '', connection._id);
                } catch (scanError) {
                    console.warn('Mutual scan failed; generating AI recommendations anyway.', scanError);
                    showInfoToast({ message: 'Mutual scan failed. Generating AI recommendations anyway.' });
                }
            }
            // Kick off AI generation — backend returns immediately with status 'generating'
            await generateAiRecommendationsHandler(connection._id);

            // Sidebar also polls for this connection, but pollUntilDone ensures the
            // table row updates even if the sidebar is closed before AI finishes.
            void pollUntilDone(connection._id);
        } catch (error: any) {
            const status = error?.response?.status;
            if (status === 429) {
                showInfoToast({ message: 'Daily AI recommendations limit exceeded.' });
            } else {
                console.error('Error while scanning mutuals / generating AI recommendations:', error);
                showErrorToast({ message: 'Error while scanning mutuals / generating AI recommendations.' });
            }
            patchConnection(connection._id, { aiRecommendationsStatus: 'error' as any });
        }
    }

    const [isAllSelected, setIsAllSelected] = useState<boolean>(false)
    const [ connectionsLoading, setConnectionsLoading ] = useState<boolean>(false)
    const [autotags] = useState<ITag[]>(
        [
            {
              _id: "New Connection",
              title: "New Connection",
              color: "#5964ff",
              isForbidden: true,
            },
            {
              _id: "LinkedIn 1st",
              title: "LinkedIn 1st",
              color: "#5964ff",
              isForbidden: true,
            },
            {
              _id: "Disconnected",
              title: "Disconnected",
              color: "#8C96AD",
              isForbidden: true,
            },
            {
              _id: "LinkedIn",
              title: "LinkedIn",
              color: "#5964ff",
              isForbidden: true,
            },
            {
              _id: "Prospect",
              title: "Prospect",
              color: "#5964ff",
              isForbidden: true,
            },
            {
              _id: "CSV",
              title: "CSV",
              color: "#5964ff",
              isForbidden: true,
            },
            {
              _id: "Imported",
              title: "Imported",
              color: "#5964ff",
              isForbidden: true,
            },
            {
              _id: "Removal Scheduled",
              title: "Removal Scheduled",
              color: "#8c96ad",
              isForbidden: true,
            },
          ]
    )

    const { pageSize, total, columnsSetup } = connectionsParameters

    const localStorageColumnsSetup = window.localStorage.getItem('columns-setup') ?? null
    const visibilitySettings = localStorageColumnsSetup ? JSON.parse(localStorageColumnsSetup) : {}

    const lastSyncAtLocalStorage = window.localStorage.getItem('lastSyncAt')
    const lastSyncAt = lastSyncAtLocalStorage && lastSyncAtLocalStorage !== 'undefined' ? lastSyncAtLocalStorage : 0

    const syncConnectionsStartedLocalStorage = window.localStorage.getItem('syncConnectionsStarted')
    const syncConnectionsStarted = syncConnectionsStartedLocalStorage && syncConnectionsStartedLocalStorage !== 'undefined' ? true : false

    const connectionsFilterRef = useRef(connectionsFilter);

    const [columnWidths, setColumnWidths] = useState<ColumnWidths>({});

    const gridViewportRef = useRef<HTMLDivElement | null>(null);
    const [savedScrollLeft, setSavedScrollLeft] = useState(0);
    const observerRef = useRef<MutationObserver | null>(null);

    const [resizableKeys, setResizableKeys] = useState<string[]>([]);
    const [enrichingItems, setEnrichingItems] = useState<Array<{connectionId: string, type: EnrichmentType}>>([]);
    const [enrichmentNoData, setEnrichmentNoData] = useState<Array<{connectionId: string, type: EnrichmentType}>>([]);

    const [loadingConversation, setLoadingConversation] = useState<string | null>(null)

    const resizableTimeouts = useRef<{ [key: string]: NodeJS.Timeout }>({});

    const connectionsRef = useRef(connections);
    useEffect(() => {
        connectionsRef.current = connections;
    }, [connections]);

    const allOnPageSelected = connections.length > 0 
        && connections.every((conn: IConnection) => selectedConnections.some((sel: IConnection) => sel._id === conn._id));

    const handleColumnResize = (idx: number, width: number) => {
        setColumnWidths(prev => {
            const updated = { ...prev, [columns[idx].key]: width }
            localStorage.setItem('connection-table-widths', JSON.stringify(updated))
            return updated
        });
    };

    useEffect(() => {
        const saved = localStorage.getItem('connection-table-widths')
        if (saved) setColumnWidths(JSON.parse(saved))
    }, [])

    useEffect(() => {
        // Filtering passed as location state
        if(
            note ||
            tag ||
            noTags ||
            industry ||
            searchByLocation ||
            searchByJobTitle ||
            birthDateFrom ||
            birthDateTo ||
            selectedUsersMutual ||
            isAutotagsFilterOr ||
            autotagsFilter ||
            conversationStatus ||
            hasJobChanges ||
            hasCompanyChanges ||
            connectionIdsFilter
        ) {
            const isAiChatIdsFilter =
                Array.isArray(connectionIdsFilter) &&
                connectionIdsFilter.length > 0 &&
                connectionIdsFilterMeta?.source === 'ai-chat';

            if (isAiChatIdsFilter) {
                const prevFilter: any = connectionsFilterRef.current || {};
                const prevMeta: any = prevFilter?.connectionIdsFilterMeta || {};

                const incomingGroups = Array.isArray((connectionIdsFilterMeta as any)?.groups)
                    ? (connectionIdsFilterMeta as any).groups
                    : null;

                const prevGroups = Array.isArray(prevMeta?.groups)
                    ? prevMeta.groups
                    : (
                        prevMeta?.source === 'ai-chat' && Array.isArray(prevFilter?.connectionIdsFilter) && prevFilter.connectionIdsFilter.length
                            ? [{
                                id: `${prevMeta?.title || ''}::${prevFilter.connectionIdsFilter.join(',')}`,
                                title: prevMeta?.title,
                                connectionIds: prevFilter.connectionIdsFilter
                            }]
                            : []
                    );

                const incomingGroupId = `${connectionIdsFilterMeta?.title || ''}::${connectionIdsFilter.join(',')}`;
                const nextGroups = incomingGroups
                    ? incomingGroups
                    : (prevGroups.some((g: any) => g?.id === incomingGroupId)
                        ? prevGroups
                        : [
                            ...prevGroups,
                            {
                                id: incomingGroupId,
                                title: connectionIdsFilterMeta?.title,
                                connectionIds: connectionIdsFilter
                            }
                        ]);

                const unionIds = Array.from(
                    new Set(
                        nextGroups
                            .flatMap((g: any) => (Array.isArray(g?.connectionIds) ? g.connectionIds : []))
                            .filter(Boolean)
                    )
                );

                setConnectionsFilterHandler({
                    ...prevFilter,
                    connectionIdsFilter: unionIds,
                    connectionIdsFilterMeta: {
                        source: 'ai-chat',
                        title: 'AI searches',
                        groups: nextGroups
                    } as any,
                    sortClicked: false,
                });
                return;
            }

            setConnectionsFilterHandler({
                ...initialConnectionsFilter,
                ...note ? { note: { value: note, label: note } } : {},
                ...tag ? { selectedTags: [tag] } : {},
                ...noTags ? { noTags: noTags } : {},
                ...industry ? { industry: { value: industry, label: industry }} : {},
                ...birthDateFrom ? { birthDateFrom } : {},
                ...birthDateTo ? { birthDateTo } : {},
                ...isAutotagsFilterOr ? { isAutotagsFilterOr } : {},
                ...autotagsFilter ? { autotagsFilter } : {},
                ...selectedUsersMutual ? { selectedUsersMutual } : {},
                ...conversationStatus ? { conversationStatus } : {},
                ...hasJobChanges ? { hasJobChanges } : {},
                ...hasCompanyChanges ? { hasCompanyChanges } : {},
                ...connectionIdsFilter ? { connectionIdsFilter } : {},
                ...connectionIdsFilterMeta ? { connectionIdsFilterMeta } : {},
                ...locationSearch?.length || jobTitleSearch?.length ? {
                    searchValues: [{
                        uid: getID(), // unique identifier
                        searchValue: locationSearch || jobTitleSearch,
                        isValueSearchOr: 'yes',
                        searchByName: 'no',
                        searchByHeadline: locationSearch?.length ? 'no' : 'yes',
                        searchByJobTitle: locationSearch?.length ? 'no' : 'yes',
                        searchByCompany: 'no',
                        searchByLocation: locationSearch?.length ? 'yes' : 'no',
                        searchByNote: 'no',
                        isOutterValueSearchOr: 'no',
                    }]
                } : {}
            })
        }
    }, [])

    useEffect(() => {
        connectionsFilterRef.current = connectionsFilter; // updating reference to have fresh object
    }, [connectionsFilter])

    //Clear router location state
    // useEffect(() => {
    //     if (location.state) navigate(location.pathname, { state: null })
    // }, [navigate, location])

    //add another for is fetching depending on the slug

    const getSortParams = (header: string, currentSort: string): string => {
        const direction = currentSort.split('.')[1] || currentSort

        switch (header) {
          case 'Name': {
            if(direction === '' || direction === 'za') return 'az'
            return 'za'
          }
          case 'Company': {
            if(direction === '' || direction === 'az') return 'company.za'
            else return 'company.az'
          }
          case 'Company headcount': {
            if(direction === '' || direction === 'az') return 'companySize.za'
            else return 'companySize.az'
          }
          case 'Headline': {
            if(direction === '' || direction === 'az') return 'headline.za'
            else return 'headline.az'
          }
          case 'Job title': {
            if(direction === '' || direction === 'az') return 'jobTitle.za'
            else return 'jobTitle.az'
          }
          case 'Location': {
            if(direction === '' || direction === 'az') return 'location.za'
            else return 'location.az'
          }
          case 'Date Connected': {
            if(direction === '' || direction === 'oldest') return 'newest'
            else return 'oldest'
          }
          case 'Followers': {
            if (direction === '' || direction === 'az') return 'followerCount.za'
            else return 'followerCount.az'
          }
          case 'Li Email': {
            if (direction === '' || direction === 'az') return 'email.za'
            else return 'email.az'
          }
          case 'Last Messaged': {
            if (direction === '' || direction === 'az' || direction === 'asc') return 'lastMessaged.desc'
            else return 'lastMessaged.asc'
          }
          case 'Personal email': {
            if (direction === '' || direction === 'az' || direction === 'asc') return 'enrichedPrivateEmails.desc'
            else return 'enrichedPrivateEmails.asc'
          }
          case 'Business email': {
            if (direction === '' || direction === 'az' || direction === 'asc') return 'enrichedBusinessEmails.desc'
            else return 'enrichedBusinessEmails.asc'
          }
          case 'Phone number': {
            if (direction === '' || direction === 'az' || direction === 'asc') return 'enrichedPhones.desc'
            else return 'enrichedPhones.asc'
          }
          case 'Li Phone': {
            if (direction === '' || direction === 'az') return 'phoneNumber.za'
            else return 'phoneNumber.az'
          }
          default:
            return ''
        }
    };

    const sortClickHandler = (props: any) => {
        if (tableRef.current) {
            const grid = tableRef.current.querySelector('.rdg') as HTMLDivElement;
            if (grid) {
                setSavedScrollLeft(grid.scrollLeft);
            }
        }
        const currentSearchBy = connectionsFilterRef.current?.searchBy
        // If clicking on a different column, reset sort
        const currentSort = (currentSearchBy === props) ? (connectionsFilterRef.current?.sort || '') : ''
        const sortParams = getSortParams(props, currentSort)

        const search = {
            ...connectionsFilterRef.current,
            sort: sortParams,
            sortClicked: true,
            searchBy: props
        }

        setConnectionsFilterHandler(search)
    };

    useEffect(() => {
        if (connectionsLoading) return; // Only observe after loading finishes

        if (tableRef.current) {
            observerRef.current?.disconnect();

            observerRef.current = new MutationObserver(() => {
                const grid = tableRef.current?.querySelector('.rdg') as HTMLDivElement;
                if (grid) {
                    grid.scrollLeft = savedScrollLeft;
                    observerRef.current?.disconnect();
                }
            });

            observerRef.current.observe(tableRef.current, { childList: true, subtree: true });
        }

        return () => observerRef.current?.disconnect();
    }, [connectionsLoading, savedScrollLeft]);

    useEffect(() => {
        if('getConnectionsPaginate' in requestLoading) setConnectionsLoading(requestLoading?.['getConnectionsPaginate'])
    }, [requestLoading]);

    const selectAllCheckboxHandler = () => {
        const selectedIds = !isAllSelected ? [...connections] : []
        setIsAllSelected(!isAllSelected)
        selectedConnectionsChange(selectedIds)
    }

    useEffect(() => {
        selectedAllConnectionsChange(isAllSelected)
    }, [isAllSelected]);

    useEffect(() => {
        if (selectedConnections.length === 0 && isAllSelected) {
            setIsAllSelected(false);
        }
    }, [selectedConnections.length]);

    useEffect(() => {
        if(enrichingConnections && enrichingConnections.length > 0 && enrichingTransactionId) {
            setEnrichingItems([...enrichingConnections])
        }
    }, [enrichingConnections, enrichingTransactionId])

    // Sync sidebar-initiated enrichments into local enrichingItems so that
    // the columns useMemo (which depends on enrichingItems) correctly shows
    // the spinner and disables the other two enrichment columns for that row.
    const prevSidebarEnrichingRef = useRef<Array<{connectionId: string; type: string}>>([]);
    useEffect(() => {
        const prev = prevSidebarEnrichingRef.current;
        const curr = sidebarEnrichingConnections;
        prevSidebarEnrichingRef.current = curr;

        if (curr.length > 0) {
            setEnrichingItems(prevItems => {
                const toAdd = curr
                    .filter((s: {connectionId: string; type: string}) => !prevItems.some(p => p.connectionId === s.connectionId && p.type === s.type))
                    .map((s: {connectionId: string; type: string}) => ({ connectionId: s.connectionId, type: s.type as EnrichmentType }));
                return toAdd.length > 0 ? [...prevItems, ...toAdd] : prevItems;
            });
        } else if (prev.length > 0) {
            // Remove items that were added by sidebar once sidebar clears them
            setEnrichingItems(prevItems =>
                prevItems.filter(p => !prev.some((s: {connectionId: string; type: string}) => s.connectionId === p.connectionId && s.type === p.type))
            );
        }
    }, [sidebarEnrichingConnections])

    useEffect(() => {
        if (
            enrichedTransaction &&
            Array.isArray(enrichedTransaction.enrichedData)
        ) {
            setEnrichingItems(prev => {
                let updated = prev;
                enrichedTransaction.enrichedData.forEach((enriched: any) => {
                    // Check for each enrichment type
                    if (enriched.enrichedPrivateEmails && enriched.enrichedPrivateEmails.length > 0) {
                        updated = updated.filter(item => !(item.connectionId === enriched._id && item.type === EnrichmentType.PRIVATE_EMAIL));
                    }
                    if (enriched.enrichedBusinessEmails && enriched.enrichedBusinessEmails.length > 0) {
                        updated = updated.filter(item => !(item.connectionId === enriched._id && item.type === EnrichmentType.BUSINESS_EMAIL));
                    }
                    if (enriched.enrichedPhones && enriched.enrichedPhones.length > 0) {
                        updated = updated.filter(item => !(item.connectionId === enriched._id && item.type === EnrichmentType.PHONE));
                    }
                });
                return updated;
            });

            setEnrichmentNoData(prev => {
                let updated = [...prev];
                enrichedTransaction.enrichedData.forEach((enriched: any) => {
                    if (enrichedTransaction.privateEmailChecked &&
                        (!enriched.enrichedPrivateEmails || enriched.enrichedPrivateEmails.length === 0)
                    ) {
                        if (!updated.some(item => item.connectionId === enriched._id && item.type === EnrichmentType.PRIVATE_EMAIL)) {
                            updated.push({ connectionId: enriched._id, type: EnrichmentType.PRIVATE_EMAIL });
                        }
                    }
                    if (enrichedTransaction.businessEmailChecked &&
                        (!enriched.enrichedBusinessEmails || enriched.enrichedBusinessEmails.length === 0)
                    ) {
                        if (!updated.some(item => item.connectionId === enriched._id && item.type === EnrichmentType.BUSINESS_EMAIL)) {
                            updated.push({ connectionId: enriched._id, type: EnrichmentType.BUSINESS_EMAIL });
                        }
                    }
                    if (enrichedTransaction.phoneChecked &&
                        (!enriched.enrichedPhones || enriched.enrichedPhones.length === 0)
                    ) {
                        if (!updated.some(item => item.connectionId === enriched._id && item.type === EnrichmentType.PHONE)) {
                            updated.push({ connectionId: enriched._id, type: EnrichmentType.PHONE });
                        }
                    }
                });
                return updated;
            });
        }

        if(enrichedTransaction?.enriched && 
            enrichedTransaction?._id === enrichingTransactionId){ // if they are different, it means that new enrichment is started
            setEnrichingItems([]);  
        }
    }, [enrichedTransaction, enrichingConnections]);

    const selectCheckboxHandler = (e: React.MouseEvent<HTMLElement>, connection: IConnection) => {
        e.stopPropagation()

        if(isAllSelected) return showInfoToast({ message: 'You can\'t use this action while all contacts are selected.'})

        const index = selectedConnections.findIndex(selectedConnection => selectedConnection._id === connection._id)
        let updatedIds = []

        if (index !== -1) {
            updatedIds = selectedConnections.filter((item: any) => String(item._id) !== String(connection._id));
            selectedConnectionsChange(updatedIds);
            setIsAllSelected(false)
        } else {
            updatedIds = [...selectedConnections, connection];
            selectedConnectionsChange(updatedIds);
            setIsAllSelected(false)
        }
    }

    const noteClickHandler = (e: React.MouseEvent<HTMLElement>, connection: IConnection) => {
        e.stopPropagation();
        openSidebar(SidebarTypes.NOTE_EDIT_CREATE, { connectionId: connection._id, existingNotes: connection.notes,
            onCloseHandler: () => closeSidebar(SidebarTypes.NOTE_EDIT_CREATE), 
           // onCreateHandler: onreateOrUpdateNoteHandler
        })
    }

    const companyClickHandler = (e: React.MouseEvent<HTMLElement>, companyId: string | undefined) => {
        e.stopPropagation();
        if(companyId) setCompanyOverviewModal(companyId)
    }
    //LD-707-commented out
    /*const onreateOrUpdateNoteHandler = async ( noteId: string | null, note: ICreateNote, updateNotes?: (notes: any) => void ) => {
        const result = await createUpdateNoteHandler(noteId, note);
        if (result && updateNotes) {
            updateNotes(result.notes);
        }
    };*/


    const selectOnThisPageHadler = () => {
        const newConnections = connectionsRef.current.filter((connection: IConnection) => !selectedConnections.some(selectedConnection => connection._id === selectedConnection._id))
        setIsAllSelected(false);
        selectedAllConnectionsChange(false);
        selectedConnectionsChange([...selectedConnections, ...newConnections])
    }

    const clearHandler = () => {
        setIsAllSelected(false)   
        selectedAllConnectionsChange(false)
        selectedConnectionsChange([])
    }

    const infoHandler = (connection: any) => {
        const version = connection?.users?.filter((e: any) => e?._id === user?._id);
        const isLinkedinFirst = isLNFirst(connection, user?._id);
        const lastMessagedAt = connection?.lastMessagedAtArray
        ?.filter((item: any) => user && item._id === user?._id)
        ?.sort((a: any, b: any) => new Date(b.lastMessagedAt).getTime() - new Date(a.lastMessagedAt).getTime())[0];
        const autoTag = connection?.autoTagsArray?.find((tag: { _id: any }) => tag?._id === user._id)?.autoTags?.[0];
        const isDisconnected = autoTag?.title === 'Disconnected';
        let enrichedPrivateEmails = connection?.enrichedPrivateEmails?.length ? connection.enrichedPrivateEmails[connection.enrichedPrivateEmails.length - 1]?.enrichedValue : '';
        let enrichedBusinessEmails = connection?.enrichedBusinessEmails?.length ? connection.enrichedBusinessEmails[connection.enrichedBusinessEmails.length - 1]?.enrichedValue : '';
        let enrichedPhones = connection?.enrichedPhones?.length ? connection.enrichedPhones[connection.enrichedPhones.length - 1]?.enrichedValue : '';

        if(enrichedTransaction) {
            const enrichedData = enrichedTransaction.enrichedData.find((enriched: any) => enriched._id === connection._id);
            if(enrichedData) {
                enrichedPrivateEmails = enrichedPrivateEmails ? enrichedPrivateEmails : enrichedData.enrichedPrivateEmails?.length ? enrichedData.enrichedPrivateEmails[enrichedData.enrichedPrivateEmails.length - 1]?.enrichedValue : '';
                enrichedBusinessEmails = enrichedBusinessEmails ? enrichedBusinessEmails : enrichedData.enrichedBusinessEmails?.length ? enrichedData.enrichedBusinessEmails[enrichedData.enrichedBusinessEmails.length - 1]?.enrichedValue : '';
                enrichedPhones = enrichedPhones ? enrichedPhones : enrichedData.enrichedPhones?.length ? enrichedData.enrichedPhones[enrichedData.enrichedPhones.length - 1]?.enrichedValue : '';
            }
        }

        return {
            version,
            isLinkedinFirst,
            lastMessagedAt,
            autoTag,
            isDisconnected,
            enrichedPrivateEmails,
            enrichedBusinessEmails,
            enrichedPhones
        };
    };

    useEffect(() => {
        // if connections are updated notify other components
        if(connections && selectedConnections){
            const updatedSelectedConnections = selectedConnections.map((sc: any) => connections.find((c: any) => c._id === sc._id) || sc)
            selectedConnectionsChange(updatedSelectedConnections);
        }
    }, [connections]);

    const Tooltip: React.FC<{ text: string; children: React.ReactNode }> = ({ text, children }) => {
        return (
            <TooltipComponent>
                {children}
                <div><span>{text}</span></div>
            </TooltipComponent>
        );
    };

    const getSortDirection = (columnName: string, currentSort: string): string => {
        if (!currentSort) return 'az';

        switch (columnName) {
            case 'Name':
                return currentSort === 'za' ? 'za' : 'az';
            case 'Headline':
            case 'Job title':
            case 'Company':
            case 'Company headcount':
            case 'Location':
            case 'Li Email':
            case 'Li Phone':
            case 'Followers':
                const direction = currentSort.split('.')[1];
                return direction === 'za' ? 'za' : 'az';
            case 'Date Connected':
                return currentSort === 'newest' ? 'za' : 'az';
            case 'Personal email':
            case 'Business email':
            case 'Phone number':
            case 'Last Messaged':
                const sortDirection = currentSort.split('.')[1];
                return sortDirection === 'desc' ? 'za' : 'az';
            default:
                return 'az';
        }
    };

    const Sort: React.FC<{ props?: string, arrow: boolean, direction: string }> = ({ props, arrow, direction }) => {
        return (
            <SortComponent alignItems onClick={() => sortClickHandler(props)} >
                <Text $lighter $heading6>{props}</Text>
                {arrow && <DropdownArrow rotateArrow={direction} />}
            </SortComponent>
        )
    };

    const UserInfo: React.FC<{ user: any; autotag: any; lastMessaged: any; removedFromLD: any; }> = ({ user, autotag, lastMessaged, removedFromLD }) => {
        let tag = autotags.find((tag) => tag.title === autotag?.title);
        if(tag){
            tag.removedFromLD = removedFromLD;
        }
        return (
            <InfoBoxColumn gap='12px'>
                <Row gap='8px'>
                    <ProfilePhotoColumn>
                    <ProfilePhoto 
                    source={user?.profilePicture} 
                    capitalLetters={`${(user?.firstName || '').charAt(0).toUpperCase()}${(user?.lastName || '').charAt(0).toUpperCase()}`}  />
                    </ProfilePhotoColumn>

                    <ColumnComponent>
                        <Text>{user?.firstName + ' ' + user?.lastName}</Text>
                        <TextDescription>{`Last msg: ${lastMessaged?.lastMessagedAt ? moment(lastMessaged?.lastMessagedAt).format('MMM D, YYYY [at] HH:mm') : `n/a`}`}</TextDescription>
                        {tag && <AppTag tag={tag}/> }
                    </ColumnComponent>
                </Row>
            </InfoBoxColumn>
        );
    };

    // Regular function (NOT a React component) so that calling it inline in renderCell
    // never causes unmount/remount. Defining it as `const EnrichedCell: React.FC` inside
    // the parent component body means every dep-triggered re-run of the `initialColumns`
    // useMemo captures a *new* function identity → React sees a different component type
    // → unmounts and remounts those cells (visible blink). A plain function call avoids that.
    const renderEnrichedCell = (row: any, infoKey: keyof InfoType, enrichmentType: keyof typeof EnrichmentType, label: string) => {
        const info: InfoType | undefined = infoHandler(row);
        const enrichedValue = info?.[infoKey];
        const isEnriching = enrichingItems.some(item => item.connectionId === row._id && item.type === EnrichmentType[enrichmentType]);
        const isNoData = enrichmentNoData.some(item => item.connectionId === row._id && item.type === EnrichmentType[enrichmentType]);
        const isAnyEnriching = enrichingItems.length > 0;
        const disabled = isAnyEnriching && !isEnriching;

        if (isEnriching) return <LoadingSpinner />

        if (isNoData) {
            let noDataText = '';
            if (enrichmentType === 'PRIVATE_EMAIL') noDataText = 'No personal email found';
            else if (enrichmentType === 'BUSINESS_EMAIL') noDataText = 'No business email found';
            else if (enrichmentType === 'PHONE') noDataText = 'No phone number found';
            else noDataText = 'No data found';
            return (
                <EnrichActionContainer>
                    <span style={{ marginRight: 6 }}><SvgFragment type="errorLight" /></span>
                    <Text>{noDataText}</Text>
                </EnrichActionContainer>
            );
        }

        return enrichedValue ? (
            <EnrichedValueCellWrap onClick={(e) => e.stopPropagation()}>
                <span className="enriched-cell-value-text">{enrichedValue}</span>
                <RefreshPathsReveal className="refresh-paths">
                    <RefreshPathsButton
                        type="button"
                        disabled={disabled}
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                            e.stopPropagation();
                            if (disabled) return;
                            enrichClickHandler(e, row, EnrichmentType[enrichmentType]);
                        }}
                    >
                        <SvgFragment type="reload" />
                        <RefreshPathsLabel>Enrich again</RefreshPathsLabel>
                    </RefreshPathsButton>
                </RefreshPathsReveal>
            </EnrichedValueCellWrap>
        ) : (
            <EnrichActionContainer>
                <EnrichActionText 
                    disabled={disabled}
                    icon="enrich" 
                    onClickHandler={(e: React.MouseEvent<HTMLElement>) => {
                        if (disabled) return;
                        enrichClickHandler(e, row, EnrichmentType[enrichmentType]);
                    }}
                >
                    {`Find ${label}`}
                </EnrichActionText>
            </EnrichActionContainer>
        );
    };

    const DropdownArrow: React.FC<{ rotateArrow?: string, hoverEffect?: boolean }> = ({ rotateArrow, hoverEffect }) =>
        <StyledSVG rotateArrow={rotateArrow} hoverEffect={hoverEffect} xmlns="http://www.w3.org/2000/svg" width="12" height="7" viewBox="0 0 12 7">
            <path fill="#5964FF" fillRule="evenodd" d="M6.193 14.215l.623.6c.257.246.674.247.932 0L12 10.747l4.252 4.068c.258.247.675.247.932 0l.623-.6c.257-.247.257-.648 0-.896l-5.34-5.133c-.258-.248-.676-.248-.933 0l-5.34 5.133c-.258.247-.258.649 0 .896z" transform="translate(-209 -182) translate(30 120) translate(11 54) translate(162)" />
        </StyledSVG>

    const Preloader: React.FC<{}> = () =>
        <ColumnComponent gap='3px'>
            {Array(pageSize).fill(undefined).map((x: any, index: number) => <PreloaderRow key={index} gap="18px">
                { Array(5).fill(undefined).map((x: any, indexCol: number) => <LoaderCell key={indexCol} $width={`${100/5}%`} $height="37px"/>) }
            </PreloaderRow>)}
        </ColumnComponent>

    const startEditingCustomField = (e: React.MouseEvent<HTMLElement>, connectionId: string, fieldKey: string, currentValue: any, fieldType: string) => {
        e.stopPropagation();
        // For user field, start with empty value for search, but keep original value for save
        const initialValue = fieldType === 'user' ? '' : (currentValue ?? '');
        editingCustomFieldRef.current = { connectionId, fieldKey, value: initialValue, originalValue: currentValue ?? '', fieldType };

        if (fieldType === 'date') {
            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
            const gap = 5;
            const calendarWidth = 264; // 240px calendar + 24px padding (12px left + 12px right)
            const calendarHeight = 240; // 204px calendar + 24px padding + some extra
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            // Position horizontally
            let x = rect.left;
            if (x + calendarWidth > viewportWidth - gap) {
                x = Math.max(gap, viewportWidth - calendarWidth - gap);
            }

            // Position vertically - prefer below
            let y = rect.bottom + gap;
            if (y + calendarHeight > viewportHeight - gap) {
                // Try above
                const aboveY = rect.top - calendarHeight - gap;
                y = aboveY > gap ? aboveY : gap;
            }

            setDatePickerState({ show: true, x, y, connectionId, fieldKey });
        } else if (fieldType === 'select') {
            // For select, just trigger re-render to show the Select component
            setEditingTrigger(prev => prev + 1);
        } else if (fieldType === 'rich_text') {
            // For rich text, show portal editor
            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
            const gap = 5;
            const editorWidth = 400;
            const editorHeight = 200;
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            // Position horizontally
            let x = rect.left;
            if (x + editorWidth > viewportWidth - gap) {
                x = Math.max(gap, viewportWidth - editorWidth - gap);
            }

            // Position vertically - prefer below
            let y = rect.bottom + gap;
            if (y + editorHeight > viewportHeight - gap) {
                // Try above
                const aboveY = rect.top - editorHeight - gap;
                y = aboveY > gap ? aboveY : gap;
            }

            setRichTextState({
                show: true,
                x,
                y,
                connectionId,
                fieldKey,
                value: currentValue ?? ''
            });
        } else if (fieldType === 'user') {
            // Always start with empty input for search
            setUserInputValue('');

            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
            const gap = 5;
            const dropdownWidth = 300;
            const dropdownHeight = 200;
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;

            // Position horizontally
            let x = rect.left;
            if (x + dropdownWidth > viewportWidth - gap) {
                x = Math.max(gap, viewportWidth - dropdownWidth - gap);
            }

            // Position vertically - prefer below
            let y = rect.bottom + gap;
            if (y + dropdownHeight > viewportHeight - gap) {
                // Try above
                const aboveY = rect.top - dropdownHeight - gap;
                y = aboveY > gap ? aboveY : gap;
            }

            setUserDropdownState({
                show: true,
                x,
                y,
                connectionId,
                fieldKey,
                searchValue: ''
            });
        }

        setEditingTrigger(prev => prev + 1); // Trigger re-render
    };

    const saveCustomFieldValue = async (connectionId: string, fieldKey: string) => {
        const { value, originalValue, fieldType } = editingCustomFieldRef.current;

        // Close editing UI immediately
        editingCustomFieldRef.current = { connectionId: null, fieldKey: null, value: null, originalValue: null, fieldType: null };
        setDatePickerState({ show: false, x: 0, y: 0, connectionId: null, fieldKey: null });
        setEditingTrigger(prev => prev + 1); // Trigger re-render

        // Only save if value has changed
        if (value !== originalValue) {
            // Convert value to appropriate type before sending to backend
            let processedValue = value;

            if (fieldType === 'number') {
                // Convert string to number, or null if empty/invalid
                if (value === '' || value === null || value === undefined) {
                    processedValue = null;
                } else {
                    const numValue = parseFloat(value);
                    if (isNaN(numValue)) {
                        showErrorToast({ message: "Please enter a valid number" });
                        return;
                    }
                    processedValue = numValue;
                }
            } else if (fieldType === 'multiselect') {
                // Ensure multiselect values are arrays
                processedValue = Array.isArray(value) ? value : [];
            } else if (fieldType === 'user') {
                // For user field, keep as object - backend handles it
                processedValue = value && typeof value === 'object' ? value : null;
            }
            // Optimistic update - update UI immediately
            const updatedConnections = connectionsRef.current.map((connection: IConnection) => {
                if (connection._id === connectionId) {
                    const customFields = connection.customFields || [];
                    const existingFieldIndex = customFields.findIndex((cf: any) => cf.key === fieldKey);

                    let updatedCustomFields;
                    if (existingFieldIndex >= 0) {
                        // Update existing field
                        updatedCustomFields = [...customFields];
                        updatedCustomFields[existingFieldIndex] = {
                            ...updatedCustomFields[existingFieldIndex],
                            value: processedValue
                        };
                    } else {
                        // Add new field
                        const fieldDef = customFieldDefinitions?.find((f: any) => f.key === fieldKey);
                        if (fieldDef) {
                            updatedCustomFields = [...customFields, {
                                key: fieldKey,
                                label: fieldDef.label,
                                type: fieldDef.type,
                                value: processedValue,
                                metadata: fieldDef.metadata
                            }];
                        } else {
                            updatedCustomFields = customFields;
                        }
                    }

                    return {
                        ...connection,
                        customFields: updatedCustomFields
                    };
                }
                return connection;
            });

            setConnections(updatedConnections);

            // API call in background
            try {
                await updateCustomFieldValueHandler(connectionId, fieldKey, processedValue);
            } catch (error) {
                // Revert on error
                const revertedConnections = connectionsRef.current.map((connection: IConnection) => {
                    if (connection._id === connectionId) {
                        const customFields = connection.customFields || [];
                        const existingFieldIndex = customFields.findIndex((cf: any) => cf.key === fieldKey);

                        if (existingFieldIndex >= 0) {
                            const updatedCustomFields = [...customFields];
                            updatedCustomFields[existingFieldIndex] = {
                                ...updatedCustomFields[existingFieldIndex],
                                value: originalValue
                            };
                            return {
                                ...connection,
                                customFields: updatedCustomFields
                            };
                        }
                    }
                    return connection;
                });

                setConnections(revertedConnections);
                showErrorToast({ message: "Failed to update custom field." });
            }
        }
    };

    const saveRichTextValue = async () => {
        const { connectionId, fieldKey } = richTextState;
        if (!connectionId || !fieldKey) return;

        const value = richTextState.value;
        const originalValue = editingCustomFieldRef.current.originalValue ?? '';

        // Close editing UI immediately
        setRichTextState({ show: false, x: 0, y: 0, connectionId: null, fieldKey: null, value: '' });
        editingCustomFieldRef.current = { connectionId: null, fieldKey: null, value: null, originalValue: null, fieldType: null };

        // Only save if value has changed
        if (value !== originalValue) {
            // Optimistic update - update UI immediately
            const updatedConnections = connectionsRef.current.map((connection: IConnection) => {
                if (connection._id === connectionId) {
                    const customFields = connection.customFields || [];
                    const existingFieldIndex = customFields.findIndex((cf: any) => cf.key === fieldKey);

                    let updatedCustomFields;
                    if (existingFieldIndex >= 0) {
                        // Update existing field
                        updatedCustomFields = [...customFields];
                        updatedCustomFields[existingFieldIndex] = {
                            ...updatedCustomFields[existingFieldIndex],
                            value: value
                        };
                    } else {
                        // Add new field
                        const fieldDef = customFieldDefinitions?.find((f: any) => f.key === fieldKey);
                        if (fieldDef) {
                            updatedCustomFields = [...customFields, {
                                key: fieldKey,
                                label: fieldDef.label,
                                type: fieldDef.type,
                                value: value,
                                metadata: fieldDef.metadata
                            }];
                        } else {
                            updatedCustomFields = customFields;
                        }
                    }
                    return {
                        ...connection,
                        customFields: updatedCustomFields
                    };
                }
                return connection;
            });

            setConnections(updatedConnections);

            // API call in background
            try {
                await updateCustomFieldValueHandler(connectionId, fieldKey, value);
            } catch (error) {
                // Revert on error
                const revertedConnections = connectionsRef.current.map((connection: IConnection) => {
                    if (connection._id === connectionId) {
                        const customFields = connection.customFields || [];
                        const existingFieldIndex = customFields.findIndex((cf: any) => cf.key === fieldKey);
                        if (existingFieldIndex >= 0) {
                            const updatedCustomFields = [...customFields];
                            updatedCustomFields[existingFieldIndex] = {
                                ...updatedCustomFields[existingFieldIndex],
                                value: originalValue
                            };
                            return {
                                ...connection,
                                customFields: updatedCustomFields
                            };
                        }
                    }
                    return connection;
                });
                setConnections(revertedConnections);
                showErrorToast({ message: "Failed to update custom field." });
            }
        }
    };

    const handleDateChange = (date: Date) => {
        editingCustomFieldRef.current.value = date;
        // Auto-save after date selection
        const { connectionId, fieldKey } = editingCustomFieldRef.current;
        if (connectionId && fieldKey) {
            saveCustomFieldValue(connectionId, fieldKey);
        }
    };

    const handleCustomFieldKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>, connectionId: string, fieldKey: string) => {
        if (e.key === 'Enter') {
            // For textarea, allow Enter for new lines unless Ctrl/Cmd is pressed
            if (e.target instanceof HTMLTextAreaElement) {
                if (e.ctrlKey || e.metaKey) {
                    e.preventDefault();
                    saveCustomFieldValue(connectionId, fieldKey);
                }
                // Otherwise allow Enter for new lines
            } else {
                // For input fields, Enter saves
                e.preventDefault();
                saveCustomFieldValue(connectionId, fieldKey);
            }
        } else if (e.key === 'Escape') {
            e.preventDefault();
            cancelEditingCustomField();
        }
    };

    const handleCustomFieldChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = e.target.value;
        const { fieldType } = editingCustomFieldRef.current;

        // Apply validation based on field type
        let validatedValue = value;

        switch (fieldType) {
            case 'number':
                // Only allow digits, decimal point, and minus sign
                validatedValue = value.replace(/[^0-9.-]/g, '');
                // Ensure only one decimal point
                const parts = validatedValue.split('.');
                if (parts.length > 2) {
                    validatedValue = parts[0] + '.' + parts.slice(1).join('');
                }
                break;
            case 'phone':
                // Allow digits, spaces, parentheses, hyphens, plus sign
                validatedValue = value.replace(/[^0-9\s()\-+]/g, '');
                break;
            case 'url':
                // Basic URL validation - allow common URL characters
                validatedValue = value.replace(/[^a-zA-Z0-9\-._~:/?#[\]@!$&'()*+,;=%]/g, '');
                break;
            case 'single_line_text':
                // Remove line breaks for single line text
                validatedValue = value.replace(/[\r\n]/g, '');
                break;
            case 'rich_text':
                // Allow all characters for rich text
                validatedValue = value;
                break;
            default:
                validatedValue = value;
        }

        editingCustomFieldRef.current.value = validatedValue;

        // Update the input value if it was modified
        if (validatedValue !== value) {
            e.target.value = validatedValue;
        }
    };

    const handleSelectChange = (option: any, connectionId: string, fieldKey: string) => {
        const value = option?.value || null;
        editingCustomFieldRef.current.value = value;
        // Auto-save after selection
        if (connectionId && fieldKey) {
            saveCustomFieldValue(connectionId, fieldKey);
        }
    };

    const handleMultiSelectChange = (options: any, connectionId: string, fieldKey: string) => {
        const values = options ? options.map((opt: any) => opt.value) : [];
        editingCustomFieldRef.current.value = values;
        // Auto-save after selection
        if (connectionId && fieldKey) {
            saveCustomFieldValue(connectionId, fieldKey);
        }
    };

    const handleUserSelect = (user: IWorkspaceUser, connectionId: string, fieldKey: string) => {
        editingCustomFieldRef.current.value = user; // Store the entire user object
        setUserInputValue(`${user.firstName} ${user.lastName}`); // Show name in input
        // Auto-save after selection
        if (connectionId && fieldKey) {
            saveCustomFieldValue(connectionId, fieldKey);
        }
        cancelUserDropdownEditing();
    };

    const handleUserSearchChange = (searchValue: string) => {
        setUserDropdownState(prev => ({ ...prev, searchValue }));
    };

    const enrichClickHandler = async (e: React.MouseEvent<HTMLElement>, connection: IConnection, enrichmentType: EnrichmentType) => {
        e.stopPropagation();

        if(workspace?.enrichmentCredits <= 0) {
            enrichConnection && enrichConnection(connection, enrichmentType);
        } else {
            setEnrichingItems(prev => [...prev, { connectionId: connection._id, type: enrichmentType }]);
            // Remove any previous 'no data' for this field
            setEnrichmentNoData(prev => prev.filter(item => !(item.connectionId === connection._id && item.type === enrichmentType)));

            let enrichedTransactionId: string | undefined;
    
            const phoneChecked = enrichmentType === EnrichmentType.PHONE;
            const privateEmailChecked = enrichmentType === EnrichmentType.PRIVATE_EMAIL;
            const businessEmailChecked = enrichmentType === EnrichmentType.BUSINESS_EMAIL;
    
            // first, estimate credits
            const resultEstimate = await estimateEnrichmentCreditsHandler({
                connectionIds: [connection._id],
                csvRows: undefined,
                privateEmailChecked: privateEmailChecked,
                businessEmailChecked: businessEmailChecked,
                phoneChecked: phoneChecked,
            });
            if (resultEstimate?.enrichedTransactionId) {
                enrichedTransactionId = resultEstimate?.enrichedTransactionId;
            }
            // then, enrich
            const resultEnrich = await enrichConnectionsHandler({
                enrichedTransactionId
            });
    
            if (resultEnrich?.enrichedTransactionId) {
                const intervalId = setInterval(async () => {
                    const result = await getEnrichedTransactionByIdHandler({ enrichedTransactionId: resultEnrich.enrichedTransactionId });
                    if (result?.failed) {
                        clearInterval(intervalId);
                        showErrorToast({ message: "Enrichment failed." });
                        // Remove from enriching items
                        setEnrichingItems(prev => prev.filter(item => 
                            !(item.connectionId === connection._id && item.type === enrichmentType)
                        ));
                    } else if (result?.enriched) {
                        clearInterval(intervalId);
            
                        let hasPrivate = !!result?.privateEmailsCountAfterEnrichment;
                        let hasBusiness = !!result?.businessEmailsCountAfterEnrichment;
                        let hasPhone = !!result?.phonesCountAfterEnrichment;
                        let noDataTypes: EnrichmentType[] = [];
                        if (privateEmailChecked && !hasPrivate) noDataTypes.push(EnrichmentType.PRIVATE_EMAIL);
                        if (businessEmailChecked && !hasBusiness) noDataTypes.push(EnrichmentType.BUSINESS_EMAIL);
                        if (phoneChecked && !hasPhone) noDataTypes.push(EnrichmentType.PHONE);

                        if (noDataTypes.length > 0) {
                            setEnrichmentNoData(prev => [
                                ...prev.filter(item => !(item.connectionId === connection._id && noDataTypes.includes(item.type))),
                                ...noDataTypes.map(type => ({ connectionId: connection._id, type }))
                            ]);
                        }

                        if (!hasPrivate && !hasBusiness && !hasPhone) {
                            const notFound = [];
                            if (privateEmailChecked) notFound.push("personal email");
                            if (businessEmailChecked) notFound.push("business email");
                            if (phoneChecked) notFound.push("phone number");
                            let message = "No ";
                            if (notFound.length === 1) {
                              message += `${notFound[0]} found.`;
                            } else if (notFound.length === 2) {
                              message += `${notFound[0]} or ${notFound[1]} found.`;
                            } else if (notFound.length === 3) {
                              message += `${notFound[0]}, ${notFound[1]} or ${notFound[2]} found.`;
                            } else {
                              message += "enrichment data found.";
                            }
                            showErrorToast({ message });
                        } else {
                            let message = `1 connection successfully enriched`;
                            const details: string[] = [];
                            if (hasPrivate) details.push(`${result?.privateEmailsCountAfterEnrichment} personal email`);
                            if (hasBusiness) details.push(`${result?.businessEmailsCountAfterEnrichment} business email`);
                            if (hasPhone) details.push(`${result?.phonesCountAfterEnrichment} phone`);
                            if (details.length > 0) message += ` (${details.join(", ")}).`;
                            showSuccessToast({ message: message });
                        }
                        
                        // update connections
                        await getEnrichedConnectionsHandler({
                            connectionIds: [connection._id],
                            enrichedTransactionId: result?._id,
                        });
                        refetchWorkspace();
    
                        // Remove from enriching items
                        setEnrichingItems(prev => prev.filter(item => !(item.connectionId === connection._id && item.type === enrichmentType)));
                    }
                }, 1000);
            }
        }
    }

    // Helper function to get custom field value from connection
    const getCustomFieldValue = (connection: IConnection, fieldKey: string) => {
        const customField = connection.customFields?.find((cf: any) => cf.key === fieldKey);
        return customField?.value;
    };

    // Helper function to format custom field value based on type
    const formatCustomFieldValue = (value: any, type: string) => {
        if (!value && value !== 0 && value !== false) return '';

        switch (type) {
            case 'date':
                return moment(value).format('MMM D, YYYY');
            case 'multiselect':
                return Array.isArray(value) ? value.join(', ') : value;
            case 'number':
                return String(value);
            case 'rich_text':
                // For rich text, show first line or truncated content
                const text = String(value);
                const firstLine = text.split('\n')[0];
                return firstLine.length > 50 ? firstLine.substring(0, 50) + '...' : firstLine;
            case 'user':
                // For user field, value is the entire user object
                if (value && typeof value === 'object' && value.firstName && value.lastName) {
                    return `${value.firstName} ${value.lastName}`;
                }
                return '';
            default:
                return String(value);
        }
    };

    let minColumnWidth = 200
    // Define columns array
    const initialColumns: Column<IConnection>[] = useMemo(() => {
        const baseColumns: Column<IConnection>[] = [
        {
            key: 'userInfo',
            name: '',
            width: isSmall ? 240 : 300,
            minWidth: minColumnWidth,
            frozen: !isSmall,
            resizable: resizableKeys.includes('userInfo'),
            headerCellClass: 'user-info-cell-class',
            renderHeaderCell: () => (
                <HeaderCell onMouseEnter={() => handleHeaderMouseEnter('userInfo')}>
                    <>
                        <div style={{position: 'absolute'}}>
                            <SelectAllCheckbox
                                isCheckboxChecked={allOnPageSelected || isAllSelected}
                                pageSize={pageSize}
                                total={total}
                                paginated={connections.length}
                                isAllSelected={isAllSelected}
                                selected={selectedConnections.length}
                                label={'contacts'}
                                selectAllHandler={selectAllCheckboxHandler}
                                selectOnThisPageHadler={selectOnThisPageHadler}
                                clearHandler={clearHandler}
                                backgroundColor='white'
                            />
                        </div>
                        <HeaderPhotoNameWrapper gap='8px'>
                            {!isSmall && <Text $lighter $heading6>MSG</Text>}
                            <Text $lighter $heading6>NOTE</Text>
                            {!isSmall && <div style={{width: '20px'}} />}
                            <Sort props='Name' arrow={connectionsFilterRef.current?.searchBy === 'Name'} direction={getSortDirection('Name', connectionsFilterRef.current?.sort)}/>
                        </HeaderPhotoNameWrapper>
                    </>
                    <Resize onMouseEnter={() => handleHeaderMouseEnter('userInfo')} />
                </HeaderCell>
            ),
            cellClass: (row) => {
                const info = infoHandler(row);
                return `${info?.isDisconnected ? 'disabled-state' : 'user-info'}`;
            },
            renderCell: ({ row, column }: RenderCellProps) => {
                const info = infoHandler(row)
                const columnWidth = columnWidths[column.key] || column.width;

                return (
                    <div style={{ width: '100%', display: 'flex', alignItems: 'center' }}>
                        <div style={{paddingRight: '8px'}}><Checkbox
                            checked={selectedConnections.some((selectedConnection: IConnection) => selectedConnection._id === row._id || isAllSelected)}
                            checkboxId={row._id}
                            onClickHandler={(e: React.MouseEvent<HTMLElement>) => selectCheckboxHandler(e, row)}
                        /></div>
                        {!isSmall && <Button
                            transparent
                            disabled={Boolean(loadingConversation) || workspaceIsStarter || !info?.isLinkedinFirst || !extensionInstalled || !isLoggedToLinkedin || extensionVersion !== process.env.REACT_APP_VERSION}
                            tooltipMessage={workspaceIsStarter ? 'Upgrade to Pro to unlock this feature' : (loadingConversation && loadingConversation !== row._id) ? 'Please wait for the current conversation to load before selecting another.' : ''}
                            $smallButton
                            onClickHandler={(e: React.MouseEvent<HTMLElement>) => messageClickHandler(e, row, setLoadingConversation)}
                            $SVGtype={'message'}
                            isLoading={loadingConversation === row._id}
                        />}
                        <Button
                            transparent
                            $smallButton
                            onClickHandler={(e: React.MouseEvent<HTMLElement>) => noteClickHandler(e, row)}
                            $SVGtype={row.notes.length !== 0 ? 'noteFull' : 'note'}
                        />
                        <CellPhotoNameWrapper alignItems gap='5px'>
                            {!isSmall && <ProfilePhoto tooltipMessage={capitalLetters(row, true)} capitalLetters={capitalLetters(row)} source={row.profilePicture} />}
                            <div style={{ width: `${columnWidth - (isSmall ? 110 : 170)}px`, display: 'flex', alignItems: 'center' }}>
                                <div style={{ minWidth: 0, flex: '0 1 auto', overflow: 'hidden' }}>
                                    <Tooltip text={`${row.firstName} ${row.lastName}`}>
                                        <Text $ellipsis='0' $bold>{row.firstName} {row.lastName}</Text>
                                    </Tooltip>
                                </div>
                            </div>
                        </CellPhotoNameWrapper>
                        <ConnectionActionDropdownWrapper onClick={(e) => e.stopPropagation()}>
                            <button
                                aria-label="Open actions"
                                onClick={(e: React.MouseEvent<HTMLElement>) => openActionDropdownPortal(e, row)}
                                style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
                            >
                                <SvgFragment type='threeDots' />
                            </button>
                        </ConnectionActionDropdownWrapper>
                    </div>
                )
            }
        },
        {
            key: 'headline',
            name: <Text $lighter $heading6>Headline</Text>,
            resizable: resizableKeys.includes('headline'),
            minWidth: minColumnWidth,
            renderHeaderCell: () => (
                <HeaderCell>
                    <Sort props='Headline' arrow={connectionsFilterRef.current?.searchBy === 'Headline'} direction={getSortDirection('Headline', connectionsFilterRef.current?.sort)} />
                    <Resize onMouseEnter={() => handleHeaderMouseEnter('headline')} />
                </HeaderCell>
            ),
            cellClass: (row) => {
                const info = infoHandler(row);
                return `truncate-cell ${info?.isDisconnected ? 'disabled-state' : ''}`;
            },
            renderCell: ({ row, column }: RenderCellProps) => {
                const columnWidth = column.width;
                return (
                    <div style={{ width: `${columnWidth - 10}px` }}>
                        <Text $ellipsis='0'>{row.headline}</Text>
                    </div>
                )
            }
        },
        {
            key: 'jobTitle',
            name: <Text $lighter $heading6>Job title</Text>,
            resizable: resizableKeys.includes('jobTitle'),
            minWidth: minColumnWidth,
            renderHeaderCell: () => (
                <HeaderCell>
                    <Sort props='Job title' arrow={connectionsFilterRef.current?.searchBy === 'Job title'} direction={getSortDirection('Job title', connectionsFilterRef.current?.sort)} />
                    <Resize onMouseEnter={() => handleHeaderMouseEnter('jobTitle')} />
                </HeaderCell>
            ),
            cellClass: (row) => {
                const info = infoHandler(row);
                return `truncate-cell ${info?.isDisconnected ? 'disabled-state' : ''}`;
            },
            renderCell: ({ row, column }: RenderCellProps) => {
                const columnWidth = column.width;
                return (
                    <div style={{ width: `${columnWidth - 10}px` }}>
                        <Text $ellipsis='0'>{row.jobTitle}</Text>
                    </div>
                )
            }
        },
        {
            key: 'company',
            name: <Text $lighter $heading6>Company</Text>,
            resizable: resizableKeys.includes('company'),
            minWidth: minColumnWidth,
            cellClass: (row) => {
                const info = infoHandler(row);
                return `truncate-cell ${info?.isDisconnected ? 'disabled-state' : ''}`;
            },
            headerCellClass: 'company-header-cell-class',
            renderHeaderCell: () => (
                <HeaderCell>
                    <Sort props='Company' arrow={connectionsFilterRef.current?.searchBy === 'Company'} direction={getSortDirection('Company', connectionsFilterRef.current?.sort)}/>
                    <Resize onMouseEnter={() => handleHeaderMouseEnter('company')} />
                </HeaderCell>
            ),
            renderCell: ({ row, column }: RenderCellProps) => {
                const columnWidth = column.width;
                return (
                    <div style={{ width: `${columnWidth - 10}px` }}>{
                        row.companyId
                        ? <ActionText onClickHandler={(e: React.MouseEvent<HTMLElement>) => companyClickHandler(e, row?.companyId)}>{row.company}</ActionText>
                        : <Text>{row.company}</Text>
                    }</div>
                )
            }
        },
        {
            key: 'companySize',
            name: <Text $lighter $heading6>Company headcount</Text>,
            resizable: resizableKeys.includes('companySize'),
            minWidth: minColumnWidth,
            cellClass: (row) => {
                const info = infoHandler(row);
                return `truncate-cell ${info?.isDisconnected ? 'disabled-state' : ''}`;
            },
            renderHeaderCell: () => (
                <HeaderCell>
                    <Sort props='Company headcount' arrow={connectionsFilterRef.current?.searchBy === 'Company headcount'} direction={getSortDirection('Company headcount', connectionsFilterRef.current?.sort)} />
                    <Resize onMouseEnter={() => handleHeaderMouseEnter('companySize')} />
                </HeaderCell>
            ),
            renderCell: ({ row, column }: RenderCellProps) => {
                const columnWidth = column.width;
                const employeesNum = row?.companyEmployeesNum;
                const value =
                    typeof employeesNum === 'number'
                        ? formatNumber(employeesNum)
                        : '';
                return (
                    <div style={{ width: `${columnWidth - 10}px` }}>
                        <Text $ellipsis='0'>{value}</Text>
                    </div>
                )
            }
        },
        {
            key: 'location',
            name: <Text $lighter $heading6>Location</Text>,
            resizable: resizableKeys.includes('location'),
            minWidth: minColumnWidth,
            cellClass: (row) => {
                const info = infoHandler(row);
                return `truncate-cell ${info?.isDisconnected ? 'disabled-state' : ''}`;
            },
            renderHeaderCell: () => (
                <HeaderCell>
                    <Sort props='Location' arrow={connectionsFilterRef.current?.searchBy === 'Location'} direction={getSortDirection('Location', connectionsFilterRef.current?.sort)}/>
                    <Resize onMouseEnter={() => handleHeaderMouseEnter('location')} />
                </HeaderCell>
            ),
            renderCell: ({ row, column }: RenderCellProps) => {
                const columnWidth = column.width;
                return (
                    <div style={{ width: `${columnWidth - 10}px` }}>
                        <Text $ellipsis='0'>{row.location || row.country}</Text>
                    </div>
                )
            }
        },
        {
            key: 'enrichedPrivateEmails',
            name: <Text $lighter $heading6>Personal email</Text>,
            resizable: resizableKeys.includes('enrichedPrivateEmails'),
            minWidth: minColumnWidth,
            cellClass: (row) => {
                const info = infoHandler(row);
                return `truncate-cell ${info?.isDisconnected ? 'disabled-state' : ''}`;
            },
            renderHeaderCell: () => (
                <HeaderCell>
                    <Sort props='Personal email' arrow={connectionsFilterRef.current?.searchBy === 'Personal email'} direction={getSortDirection('Personal email', connectionsFilterRef.current?.sort)}/>
                    <Resize onMouseEnter={() => handleHeaderMouseEnter('enrichedPrivateEmails')} />
                </HeaderCell>
            ),
            renderCell: ({ row }: RenderCellProps) => renderEnrichedCell(row, 'enrichedPrivateEmails', 'PRIVATE_EMAIL', 'private email')
        },
        {
            key: 'enrichedBusinessEmails',
            name: <Text $lighter $heading6>Business email</Text>,
            resizable: resizableKeys.includes('enrichedBusinessEmails'),
            minWidth: minColumnWidth,
            cellClass: (row) => {
                const info = infoHandler(row);
                return `truncate-cell ${info?.isDisconnected ? 'disabled-state' : ''}`;
            },
            renderHeaderCell: () => (
                <HeaderCell>
                    <Sort props='Business email' arrow={connectionsFilterRef.current?.searchBy === 'Business email'} direction={getSortDirection('Business email', connectionsFilterRef.current?.sort)}/>
                    <Resize onMouseEnter={() => handleHeaderMouseEnter('enrichedBusinessEmails')} />
                </HeaderCell>
            ),
            renderCell: ({ row }: RenderCellProps) => renderEnrichedCell(row, 'enrichedBusinessEmails', 'BUSINESS_EMAIL', 'business email')
        },
        {
            key: 'enrichedPhones',
            name: <Text $lighter $heading6>Phone number</Text>,
            resizable: resizableKeys.includes('enrichedPhones'),
            minWidth: minColumnWidth,
            cellClass: (row) => {
                const info = infoHandler(row);
                return `truncate-cell ${info?.isDisconnected ? 'disabled-state' : ''}`;
            },
            renderHeaderCell: () => (
                <HeaderCell>
                    <Sort props='Phone number' arrow={connectionsFilterRef.current?.searchBy === 'Phone number'} direction={getSortDirection('Phone number', connectionsFilterRef.current?.sort)}/>
                    <Resize onMouseEnter={() => handleHeaderMouseEnter('enrichedPhones')} />
                </HeaderCell>
            ),
            renderCell: ({ row }: RenderCellProps) => renderEnrichedCell(row, 'enrichedPhones', 'PHONE', 'phone number')
        },
        {
            key: 'contactInfo',
            name: <Text $lighter $heading6>Li Email</Text>,
            resizable: resizableKeys.includes('contactInfo'),
            minWidth: minColumnWidth,
            cellClass: (row) => {
                const info = infoHandler(row);
                return `truncate-cell ${info?.isDisconnected ? 'disabled-state' : ''}`;
            },
            renderHeaderCell: () => (
                <HeaderCell>
                    <Sort props='Li Email' arrow={connectionsFilterRef.current?.searchBy === 'Li Email'} direction={getSortDirection('Li Email', connectionsFilterRef.current?.sort)}/>
                    <Resize onMouseEnter={() => handleHeaderMouseEnter('contactInfo')} />
                </HeaderCell>
            ),
            renderCell: ({ row }: RenderCellProps) => (
                <ColumnComponent>
                    <Text $ellipsis='0' showTooltip>{row.email}</Text>
                </ColumnComponent>
            )
        },
        {
            key: 'liPhone',
            name: <Text $lighter $heading6>Li Phone</Text>,
            resizable: resizableKeys.includes('liPhone'),
            minWidth: minColumnWidth,
            cellClass: (row) => {
                const info = infoHandler(row);
                return `truncate-cell ${info?.isDisconnected ? 'disabled-state' : ''}`;
            },
            renderHeaderCell: () => (
                <HeaderCell>
                    <Sort props='Li Phone' arrow={connectionsFilterRef.current?.searchBy === 'Li Phone'} direction={getSortDirection('Li Phone', connectionsFilterRef.current?.sort)}/>
                    <Resize onMouseEnter={() => handleHeaderMouseEnter('liPhone')} />
                </HeaderCell>
            ),
            renderCell: ({ row }: RenderCellProps) => (
                <Text $ellipsis='0' showTooltip>{row.phoneNumber}</Text>
            )
        },
        {
            key: 'dateConnected',
            name: <Text $lighter $heading6>Date Connected</Text>,
            resizable: resizableKeys.includes('dateConnected'),
            minWidth: minColumnWidth,
            cellClass: (row) => {
                const info = infoHandler(row);
                return `truncate-cell ${info?.isDisconnected ? 'disabled-state' : ''}`;
            },
            renderHeaderCell: () => (
                <HeaderCell>
                    <Sort props='Date Connected' arrow={connectionsFilterRef.current?.searchBy === 'Date Connected'} direction={getSortDirection('Date Connected', connectionsFilterRef.current?.sort)} />
                    <Resize onMouseEnter={() => handleHeaderMouseEnter('dateConnected')} />
                </HeaderCell>
            ),
            renderCell: ({ row }: RenderCellProps) => {
                // Find connectedAt for current user from connectedAtArray
                const userConnectedAt = row?.connectedAtArray?.find((item: any) => currentUserId && item._id === currentUserId)?.connectedAt;
                // Fallback to row.connectedAt if connectedAtArray doesn't have current user's entry
                const connectedAt = userConnectedAt || '';
                return (
                    <Text>{connectedAt ? moment(connectedAt).format('MMM D, YYYY [at] HH:mm') : ''}</Text>
                );
            }
        },
        {
            key: 'languages',
            name: <Text $lighter $heading6>Languages</Text>,
            resizable: resizableKeys.includes('languages'),
            minWidth: minColumnWidth,
            cellClass: (row) => {
                const info = infoHandler(row);
                return `truncate-cell ${info?.isDisconnected ? 'disabled-state' : ''}`;
            },
            renderHeaderCell: () => (
                <HeaderCell>
                    <Text $lighter $heading6>Languages</Text>
                    <Resize onMouseEnter={() => handleHeaderMouseEnter('languages')} />
                </HeaderCell>
            ),
            renderCell: ({ row }: RenderCellProps) => (
                <Row>
                    <Text>{row?.languages?.join(', ')}</Text>
                </Row>
            )
        },
        {
            key: 'dateOfBirth',
            name: <Text $lighter $heading6>Date of Birth</Text>,
            resizable: resizableKeys.includes('dateOfBirth'),
            minWidth: minColumnWidth,
            cellClass: (row) => {
                const info = infoHandler(row);
                return `${info?.isDisconnected ? 'disabled-state' : ''}`;
            },
            renderHeaderCell: () => (
                <HeaderCell>
                    <Text $lighter $heading6>Date of Birth</Text>
                    <Resize onMouseEnter={() => handleHeaderMouseEnter('dateOfBirth')} />
                </HeaderCell>
            ),
            renderCell: ({ row }: RenderCellProps) => (
                <Text>{row?.birthDate ? moment(row.birthDate.month + " " + row.birthDate.day, 'MM DD').format('MMM D') : ''}</Text>
            )
        },
        {
            key: 'followers',
            name: <Text $lighter $heading6>Followers</Text>,
            resizable: resizableKeys.includes('followers'),
            minWidth: minColumnWidth,
            cellClass: (row) => {
                const info = infoHandler(row);
                return `truncate-cell ${info?.isDisconnected ? 'disabled-state' : ''}`;
            },
            renderHeaderCell: () => (
                <HeaderCell>
                    <Sort props='Followers' arrow={connectionsFilterRef.current?.searchBy === 'Followers'} direction={getSortDirection('Followers', connectionsFilterRef.current?.sort)}/>
                    <Resize onMouseEnter={() => handleHeaderMouseEnter('followers')} />
                </HeaderCell>
            ),
            renderCell: ({ row }: RenderCellProps) => (
                <Text>{row?.followerCount ? formatNumber(row?.followerCount) : ''}</Text>
            )
        },
        {
            key: 'lastMessaged',
            name: <Text $lighter $heading6>Last messaged</Text>,
            resizable: resizableKeys.includes('lastMessaged'),
            minWidth: minColumnWidth,
            cellClass: (row) => {
                const info = infoHandler(row);
                return `truncate-cell ${info?.isDisconnected ? 'disabled-state' : ''}`;
            },
            renderHeaderCell: () => (
                <HeaderCell>
                    <Sort props='Last Messaged' arrow={connectionsFilterRef.current?.searchBy === 'Last Messaged'} direction={getSortDirection('Last Messaged', connectionsFilterRef.current?.sort)}/>
                    <Resize onMouseEnter={() => handleHeaderMouseEnter('lastMessaged')} />
                </HeaderCell>
            ),
            renderCell: ({ row, column }: RenderCellProps) => {
                const info = infoHandler(row)
                return (
                    <StyledMessaged
                        tooltipMessage={info?.lastMessagedAt ? `${info?.lastMessagedAt?.sent ? 'Sent at ' : 'Received at '} ${moment(info?.lastMessagedAt?.info?.lastMessagedAt).format('MMM D, YYYY [at] HH:mm')}` : ''}
                        $sent={info?.lastMessagedAt?.sent}>
                        {info?.lastMessagedAt ? moment(info?.lastMessagedAt?.lastMessagedAt).fromNow() : ''}
                    </StyledMessaged>
                );
            }
        },
        {
            key: 'tags',
            name: <Text $lighter $heading6>Tags</Text>,
            resizable: resizableKeys.includes('tags'),
            minWidth: 300,
            cellClass: (row) => {
                const info = infoHandler(row);
                return `${info?.isDisconnected ? 'disabled-state' : ''}`;
            },
            renderHeaderCell: () => (
                <HeaderCell>
                    <Text $lighter $heading6>Tags</Text>
                    <Resize onMouseEnter={() => handleHeaderMouseEnter('tags')} />
                </HeaderCell>
            ),
            renderCell: ({ row, column }: RenderCellProps) => {
                const tags = showAutoTags 
                    ? row?.tags 
                    : row?.tags?.filter(tag => !tag.isForbidden)?.sort((a, b) => a?.title?.localeCompare(b?.title));
                const filteredTags = (tags || []).filter(tag => !tag.isForbidden);
                const colWidth = Math.max(300, typeof column.width === 'number' ? column.width : columnWidths['tags'] ?? 300);
                const availableWidth = Math.max(0, colWidth - 16); // account for cell horizontal padding (8px left + 8px right)
                return (
                    <div style={{ position: 'relative', width: `${availableWidth}px` }}>
                        <Row alignItems gap="5px">
                            <ConnectionTags 
                                tags={filteredTags}
                                maxWidth={availableWidth - 28}
                                connection={row}
                            />
                            <ApplyRemoveTagForm 
                                connection={row}
                                tags={filteredTags}
                                renderOnlyAddButton
                                maxWidth={Math.min(availableWidth, 350)}
                                showAddOnHover
                                dropdownBelow
                                hideEmptyState
                                usePortal
                            />
                        </Row>
                    </div>
                )
            }
        },
        ...(canShowScanMutuals ? ([{
                key: 'linkedinMutualProfiles',
                minWidth: 256,
                name: <Text $lighter $heading6>Warm Intro AI</Text>,
                resizable: resizableKeys.includes('linkedinMutualProfiles'),
                cellClass: (row: any) => {
                    const info = infoHandler(row);
                    return `mutual-connections-cell ${info?.isDisconnected ? 'disabled-state' : ''}`;
                },
                renderHeaderCell: () => (
                    <HeaderCell>
                        <WarmIntroHeaderTitle>
                            <Text $lighter $heading6>Warm Intro AI</Text>
                            <BetaTag>BETA</BetaTag>
                        </WarmIntroHeaderTitle>
                        <Resize onMouseEnter={() => handleHeaderMouseEnter('linkedinMutualProfiles')} />
                    </HeaderCell>
                ),
                renderCell: ({ row }: RenderCellProps) => {
                    if (!canUseWarmIntroAi) {
                        return (
                            <TooltipWrapper tooltipText="Upgrade to Business to unlock this feature">
                                <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                    <WarmIntroActionText
                                        icon="scan"
                                        onClickHandler={(e: React.MouseEvent<HTMLElement>) => {
                                            e.stopPropagation();
                                        }}
                                        style={{ opacity: 0.6, cursor: 'not-allowed' }}
                                    >
                                        Find intros
                                    </WarmIntroActionText>
                                </span>
                            </TooltipWrapper>
                        );
                    }
                    const aiProfilesCount = row?.linkedinAiMutualProfilesCount ?? 0;
                    const mutualProfiles = Array.isArray(row?.linkedinMutualProfiles)
                        ? row.linkedinMutualProfiles.filter((id: string) => typeof id === 'string' && id.trim().length > 0)
                        : [];
                    const count = aiProfilesCount > 0 ? aiProfilesCount : mutualProfiles.length;
                    const isGenerating = row?.aiRecommendationsStatus === 'generating';
                    const isError = row?.aiRecommendationsStatus === 'error';

                    if (isGenerating) {
                        return (
                            <ActionText
                                onClickHandler={(e: React.MouseEvent<HTMLElement>) => {
                                    e.stopPropagation();
                                    closeSidebar(SidebarTypes.CONNECTION_NETWORK);
                                    setTimeout(() => openSidebar(SidebarTypes.CONNECTION_NETWORK, { connection: row }), 0);
                                }}
                            >
                                Finding paths...
                            </ActionText>
                        );
                    }
                    if (isError) {
                        if (dailyLimitReachedAi) {
                            return (
                                <TooltipWrapper tooltipText={dailyLimitReachedMessage}>
                                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                        <WarmIntroActionText
                                            icon="reload"
                                            onClickHandler={(e: React.MouseEvent<HTMLElement>) => {
                                                e.stopPropagation();
                                            }}
                                            style={{ opacity: 0.6, cursor: 'not-allowed' }}
                                        >
                                            Try again
                                        </WarmIntroActionText>
                                    </span>
                                </TooltipWrapper>
                            );
                        }
                        return (
                            <WarmIntroActionText
                                icon="reload"
                                onClickHandler={(e: React.MouseEvent<HTMLElement>) => {
                                    e.stopPropagation();
                                    scanMutualsForRow(row);
                                }}
                            >
                                Try again
                            </WarmIntroActionText>
                        );
                    }
                    if (count === 0) {
                        if (dailyLimitReachedAi) {
                            return (
                                <TooltipWrapper tooltipText={dailyLimitReachedMessage}>
                                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                        <WarmIntroActionText
                                            icon="scan"
                                            onClickHandler={(e: React.MouseEvent<HTMLElement>) => {
                                                e.stopPropagation();
                                            }}
                                            style={{ opacity: 0.6, cursor: 'not-allowed' }}
                                        >
                                            Find intros
                                        </WarmIntroActionText>
                                    </span>
                                </TooltipWrapper>
                            );
                        }
                        return (
                            <WarmIntroActionText
                                icon="scan"
                                onClickHandler={(e: React.MouseEvent<HTMLElement>) => {
                                    e.stopPropagation();
                                    scanMutualsForRow(row);
                                }}
                            >
                                Find intros
                            </WarmIntroActionText>
                        );
                    }

                    const introPathsLabel =
                        count === 1 ? '1 intro path' : `${count} intro paths`;

                    return (
                        <IntroPathsCellWrap onClick={(e) => e.stopPropagation()}>
                            <ActionText
                                onClickHandler={(e: React.MouseEvent<HTMLElement>) => {
                                    e.stopPropagation();
                                    closeSidebar(SidebarTypes.CONNECTION_NETWORK);
                                    setTimeout(() => openSidebar(SidebarTypes.CONNECTION_NETWORK, { connection: row }), 0);
                                }}
                            >
                                {introPathsLabel}
                            </ActionText>
                            {!dailyLimitReachedAi ? (
                                <RefreshPathsReveal className="refresh-paths">
                                    <RefreshPathsButton
                                        type="button"
                                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                            e.stopPropagation();
                                            scanMutualsForRow(row);
                                        }}
                                    >
                                        <SvgFragment type="reload" />
                                        <RefreshPathsLabel>Refresh paths</RefreshPathsLabel>
                                    </RefreshPathsButton>
                                </RefreshPathsReveal>
                            ) : null}
                        </IntroPathsCellWrap>
                    );
                }
            }]) : []),
        {
            key: 'mutualConnections',
            minWidth: minColumnWidth,
            maxWidth: 200,
            name: <Text $lighter $heading6>Connected members</Text>,
            resizable: resizableKeys.includes('mutualConnections'),
            cellClass: (row) => {
                const info = infoHandler(row);
                return `mutual-connections-cell ${info?.isDisconnected ? 'disabled-state' : ''}`;
            },
            renderCell: ({ row }: RenderCellProps) => {
                if (workspaceIsStarter) return null;
                
                const isLastColumn = Object.keys(columnsOrder).at(-1) === 'mutualConnections';
                const isSoloWorkspace = workspaceUsers?.length === 1 && workspaceUsers?.[0]?._id === currentUserId;
                const users = Array.isArray(row?.users) ? row.users : [];
                const colWidth = columnWidths['mutualConnections'] ?? 200;
                const availableWidth = Math.max(0, colWidth - 16); // account for cell padding
                const step = 22; // 24px avatar with ~2px overlap
                const maxVisible = Math.max(1, Math.floor((availableWidth + 2) / step));
                const visibleUsers = users.slice(0, maxVisible);
                const hiddenCount = Math.max(0, users.length - visibleUsers.length);
                
                const handleMouseEnter = (e: React.MouseEvent<HTMLDivElement>) => {
                    if (isSoloWorkspace) return;
                    cancelInfoBoxClose();
                    const rect = e.currentTarget.getBoundingClientRect();
                    setInfoBoxState({
                        show: true,
                        x: rect.left,
                        y: rect.bottom,
                        positioned: false,
                        connection: row,
                        triggerRect: { top: rect.top, bottom: rect.bottom, left: rect.left, right: rect.right, width: rect.width, height: rect.height },
                        isLastColumn
                    });
                };

                const handleMouseLeave = () => {
                    if (isSoloWorkspace) return;
                    scheduleInfoBoxClose();
                };

                return (
                    <ConnectedMembersCell
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                    >
                        {!isSoloWorkspace && visibleUsers.map((u: any) =>
                            <OverlappingPhoto key={u?._id} className={`connection-version-${u?.version}`}
                                source={u?.profilePicture}
                                allowTooltip={false}
                                capitalLetters={`${(u?.firstName || '').charAt(0).toUpperCase()}${(u?.lastName || '').charAt(0).toUpperCase()}`}
                            />
                        )}
                        {!isSoloWorkspace && hiddenCount > 0 && (
                            <MoreMembersPill>{`+${hiddenCount} more`}</MoreMembersPill>
                        )}
                        {isSoloWorkspace && (
                            <AddMemberHoverButton
                                type="button"
                                className="add-member-button"
                                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                    e.stopPropagation();
                                    setAddMembersModal();
                                }}
                            >
                                <SvgFragment type="plusBlue" />
                                <span>Add members</span>
                            </AddMemberHoverButton>
                        )}
                    </ConnectedMembersCell>
                );
            }
        }
    ];

        // Add custom field columns from GET /profiles/custom-field-definitions (excluding select and multiselect types)
        const customFieldColumns: Column<IConnection>[] = (customFieldDefinitions || [])
            // TODO: remove this filter in V2
            .filter((field: any) =>
                field.type !== 'select' &&
                field.type !== 'multiselect' &&
                field.type !== 'boolean' &&
                field.type !== 'tag'
            )
            .map((field: any) => ({
            key: `customField_${field.key}`,
            name: <Text $lighter $heading6>{field.label}</Text>,
            resizable: true,
            minWidth: field.type === 'phone' || field.type === 'user' ? 240 : minColumnWidth,
            cellClass: (row: IConnection) => {
                const info = infoHandler(row);
                return `truncate-cell ${info?.isDisconnected ? 'disabled-state' : ''}`;
            },
            renderHeaderCell: () => (
                <HeaderCell>
                    <Text $lighter $heading6>{field.label}</Text>
                    <Resize onMouseEnter={() => handleHeaderMouseEnter(`customField_${field.key}`)} />
                </HeaderCell>
            ),
            renderCell: ({ row, column }: RenderCellProps) => {
                const value = getCustomFieldValue(row, field.key);
                const formattedValue = formatCustomFieldValue(value, field.type);
                const isEditing = editingCustomFieldRef.current.connectionId === row._id && editingCustomFieldRef.current.fieldKey === field.key;
                const isSelectType = field.type === 'select';

                // Prepare select options
                const selectOptions = isSelectType && field.metadata?.options
                    ? field.metadata.options.map((opt: string) => ({ label: opt, value: opt }))
                    : [];
                const selectValue = isSelectType && value
                    ? { label: value, value: value }
                    : null;

                return (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '100%',
                            height: '100%',
                            cursor: 'pointer'
                        }}
                        onClick={(e) => {
                            e.stopPropagation(); // Prevent DataGrid row click
                            if (!isEditing) {
                                startEditingCustomField(e, row._id, field.key, value, field.type);
                            }
                        }}
                    >
                        {isEditing && isSelectType ? (
                            <div className="custom-field-select" onClick={(e) => e.stopPropagation()}>
                                <Select
                                    placeholder={field.metadata?.placeholder || 'Select...'}
                                    isSearchable={false}
                                    options={selectOptions}
                                    value={selectValue}
                                    onChange={(option: any) => handleSelectChange(option, row._id, field.key)}
                                    onBlur={() => {
                                        // Delay to allow option selection to complete
                                        setTimeout(() => cancelEditingCustomField(), 150);
                                    }}
                                    menuPortalTarget={document.body}
                                    menuIsOpen={true}
                                    autoFocus
                                    styles={{
                                        menuPortal: (base: any) => ({ ...base, zIndex: 10200 }),
                                        control: (base: any) => ({ ...base, minHeight: '32px', height: '32px' }),
                                        valueContainer: (base: any) => ({ ...base, padding: '0 8px' }),
                                        input: (base: any) => ({ ...base, margin: 0, padding: 0 })
                                    }}
                                />
                            </div>
                        ) : (
                            <div style={{ width: '100%', overflow: 'hidden' }}>
                                {field.type === 'single_line_text' ? (
                                    isEditing ? (
                                        <CustomFieldInput
                                            type="text"
                                            defaultValue={editingCustomFieldRef.current.value}
                                            onChange={handleCustomFieldChange}
                                            onKeyDown={(e) => handleCustomFieldKeyDown(e, row._id, field.key)}
                                            onBlur={() => {
                                                const currentEditing = editingCustomFieldRef.current;
                                                const { connectionId, fieldKey } = currentEditing;

                                                if (connectionId && fieldKey) {
                                                    saveCustomFieldValue(connectionId, fieldKey);
                                                } else {
                                                    cancelEditingCustomField();
                                                }
                                            }}
                                            autoFocus
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    ) : (
                                        <Text $ellipsis='0'>
                                            {formattedValue || <span style={{ color: styles.colors.black300 }}>{field.metadata?.placeholder || 'Click to edit'}</span>}
                                        </Text>
                                    )
                                ) : field.type === 'select' ? (
                                        <Select
                                            placeholder={field.metadata?.placeholder || 'Select...'}
                                            isSearchable={false}
                                            options={selectOptions}
                                            value={selectValue}
                                            onChange={(option: any) => handleSelectChange(option, row._id, field.key)}
                                            menuPortalTarget={document.body}
                                            styles={{
                                                option: (base: any, { isSelected, isFocused }: any) => ({
                                                    ...base,
                                                    backgroundColor: isFocused ? '#F6F8FB' : 'transparent',
                                                    color: isSelected ? '#1a73e8' : base.color,
                                                    ':active': {
                                                        backgroundColor: '#F6F8FB'
                                                    }
                                                 })
                                            }}
                                        />
                                ) : field.type === 'multiselect' ? (
                                    <Select
                                        placeholder={field.metadata?.placeholder || 'Select...'}
                                        isSearchable={false}
                                        isMulti
                                        options={selectOptions}
                                        value={Array.isArray(value) ? value.map(v => ({ label: v, value: v })) : []}
                                        onChange={(options: any) => handleMultiSelectChange(options, row._id, field.key)}
                                        menuPortalTarget={document.body}
                                        styles={{
                                            option: (base: any, { isSelected, isFocused }: any) => ({
                                                ...base,
                                                backgroundColor: isFocused ? '#F6F8FB' : 'transparent',
                                                color: isSelected ? '#1a73e8' : base.color,
                                                ':active': {
                                                    backgroundColor: '#F6F8FB'
                                                }
                                            })
                                        }}
                                    />
                                ) : field.type === 'url' ? (
                                    isEditing ? (
                                        <CustomFieldInput
                                            type="text"
                                            defaultValue={editingCustomFieldRef.current.value}
                                            onChange={handleCustomFieldChange}
                                            onKeyDown={(e) => handleCustomFieldKeyDown(e, row._id, field.key)}
                                            onBlur={() => {
                                                const currentEditing = editingCustomFieldRef.current;
                                                const { connectionId, fieldKey } = currentEditing;

                                                if (connectionId && fieldKey) {
                                                    saveCustomFieldValue(connectionId, fieldKey);
                                                } else {
                                                    cancelEditingCustomField();
                                                }
                                            }}
                                            autoFocus
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    ) : (
                                        <UrlFieldContainer>
                                            <TooltipWrapper tooltipText={formattedValue || field.metadata?.placeholder || 'Click to edit'}>
                                                <div style={{ width: '100%', overflow: 'hidden' }}>
                                                    <UrlText>
                                                        {formattedValue || field.metadata?.placeholder || 'Click to edit'}
                                                    </UrlText>
                                                </div>
                                            </TooltipWrapper>
                                            {formattedValue && (
                                                <UrlActions>
                                                    <TooltipWrapper tooltipText="Visit URL">
                                                        <UrlActionButton
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                let urlToOpen = formattedValue;
                                                                // Add https:// if URL doesn't have a protocol
                                                                if (urlToOpen && !/^https?:\/\//i.test(urlToOpen)) {
                                                                    urlToOpen = `https://${urlToOpen}`;
                                                                }
                                                                window.open(urlToOpen, '_blank');
                                                            }}
                                                        >
                                                            <SvgFragment type='visitSite' />
                                                        </UrlActionButton>
                                                    </TooltipWrapper>
                                                    <TooltipWrapper tooltipText="Copy URL">
                                                        <UrlActionButton
                                                            onClick={async (e) => {
                                                                e.stopPropagation();
                                                                try {
                                                                    await navigator.clipboard.writeText(formattedValue);
                                                                    showSuccessToast({ message: 'URL copied' });
                                                                } catch (error) {
                                                                    showErrorToast({ message: 'Failed to copy URL' });
                                                                }
                                                            }}
                                                        >
                                                            <SvgFragment type='copy' />
                                                        </UrlActionButton>
                                                    </TooltipWrapper>
                                                </UrlActions>
                                            )}
                                        </UrlFieldContainer>
                                    )
                                ) : field.type === 'email' ? (
                                    isEditing ? (
                                        <CustomFieldInput
                                            type="text"
                                            defaultValue={editingCustomFieldRef.current.value}
                                            onChange={handleCustomFieldChange}
                                            onKeyDown={(e) => handleCustomFieldKeyDown(e, row._id, field.key)}
                                            onBlur={() => {
                                                const currentEditing = editingCustomFieldRef.current;
                                                const { connectionId, fieldKey } = currentEditing;

                                                if (connectionId && fieldKey) {
                                                    saveCustomFieldValue(connectionId, fieldKey);
                                                } else {
                                                    cancelEditingCustomField();
                                                }
                                            }}
                                            autoFocus
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                    ) : (
                                        <EmailFieldContainer>
                                            <TooltipWrapper tooltipText={formattedValue || field.metadata?.placeholder || 'Click to edit'}>
                                                <div style={{ width: '100%', overflow: 'hidden' }}>
                                                    <EmailText>
                                                        {formattedValue || field.metadata?.placeholder || 'Click to edit'}
                                                    </EmailText>
                                                </div>
                                            </TooltipWrapper>
                                            {formattedValue && (
                                                <EmailActions>
                                                    <TooltipWrapper tooltipText="Send Email">
                                                        <EmailActionButton
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                window.location.href = `mailto:${formattedValue}`;
                                                            }}
                                                        >
                                                            <SvgFragment type='sendMail' />
                                                        </EmailActionButton>
                                                    </TooltipWrapper>
                                                    <TooltipWrapper tooltipText="Copy Email">
                                                        <EmailActionButton
                                                            onClick={async (e) => {
                                                                e.stopPropagation();
                                                                try {
                                                                    await navigator.clipboard.writeText(formattedValue);
                                                                    showSuccessToast({ message: 'Email copied' });
                                                                } catch (error) {
                                                                    showErrorToast({ message: 'Failed to copy email' });
                                                                }
                                                            }}
                                                        >
                                                            <SvgFragment type='copy' />
                                                        </EmailActionButton>
                                                    </TooltipWrapper>
                                                </EmailActions>
                                            )}
                                        </EmailFieldContainer>
                                    )
                                ) : field.type === 'number' ? (
                                    <NumberFieldContainer>
                                        {isEditing ? (
                                            <CustomFieldInput
                                                type="number"
                                                defaultValue={editingCustomFieldRef.current.value}
                                                onChange={handleCustomFieldChange}
                                                onKeyDown={(e) => handleCustomFieldKeyDown(e, row._id, field.key)}
                                                onBlur={() => {
                                                    const currentEditing = editingCustomFieldRef.current;
                                                    const { connectionId, fieldKey } = currentEditing;

                                                    if (connectionId && fieldKey) {
                                                        saveCustomFieldValue(connectionId, fieldKey);
                                                    } else {
                                                        cancelEditingCustomField();
                                                    }
                                                }}
                                                autoFocus
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                        ) : (
                                            <>
                                                <NumberDisplay onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                                                    e.stopPropagation();
                                                    startEditingCustomField(e, row._id, field.key, value, field.type);
                                                }}>
                                                    {formattedValue || <span style={{ color: '#8C96AD' }}>{field.metadata?.placeholder || 'Add a number'}</span>}
                                                </NumberDisplay>
                                                <NumberSteppers>
                                                <NumberStepperButton
                                                    $isEditing={isEditing}
                                                    onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => {
                                                        if (isEditing) {
                                                            e.preventDefault(); // Prevent blur when clicking stepper
                                                        }
                                                    }}
                                                    onClick={async (e: React.MouseEvent<HTMLButtonElement>) => {
                                                        e.stopPropagation();

                                                        if (isEditing) {
                                                            const currentValue = Number(editingCustomFieldRef.current.value || 0);
                                                            const newValue = currentValue + 1;

                                                            editingCustomFieldRef.current.value = newValue;
                                                            // Trigger re-render to show new value
                                                            setEditingTrigger(prev => prev + 1);

                                                            try {
                                                                await updateCustomFieldValueHandler(row._id, field.key, newValue);
                                                            } catch (error) {
                                                                console.error('Error updating custom field:', error);
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <SvgFragment type="upArrow" />
                                                </NumberStepperButton>
                                                <NumberStepperButton
                                                    $isEditing={isEditing}
                                                    onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) => {
                                                        if (isEditing) {
                                                            e.preventDefault(); // Prevent blur when clicking stepper
                                                        }
                                                    }}
                                                    onClick={async (e: React.MouseEvent<HTMLButtonElement>) => {
                                                        e.stopPropagation();

                                                        if (isEditing) {
                                                            const currentValue = Number(editingCustomFieldRef.current.value || 0);
                                                            const newValue = currentValue - 1;

                                                            editingCustomFieldRef.current.value = newValue;
                                                            // Trigger re-render to show new value
                                                            setEditingTrigger(prev => prev + 1);

                                                            try {
                                                                await updateCustomFieldValueHandler(row._id, field.key, newValue);
                                                            } catch (error) {
                                                                console.error('Error updating custom field:', error);
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <SvgFragment type="downArrow" />
                                                </NumberStepperButton>
                                            </NumberSteppers>
                                        </>
                                        )}
                                    </NumberFieldContainer>
                                ) : field.type === 'phone' ? (
                                    <PhoneFieldContainer>
                                        <PhoneInput
                                            value={isEditing ? (editingCustomFieldRef.current.value || '') : (formattedValue || '')}
                                            onChange={(value) => {
                                                if (isEditing) {
                                                    editingCustomFieldRef.current.value = value;
                                                    // Don't trigger re-render to avoid layout shift
                                                } else {
                                                    // If not editing, start editing and set the value
                                                    const syntheticEvent = {
                                                        stopPropagation: () => {},
                                                        preventDefault: () => {}
                                                    } as React.MouseEvent<HTMLElement>;
                                                    startEditingCustomField(syntheticEvent, row._id, field.key, value, field.type);
                                                    editingCustomFieldRef.current.value = value;
                                                }
                                            }}
                                            onEnter={() => {
                                                if (isEditing) {
                                                    const currentEditing = editingCustomFieldRef.current;
                                                    const { connectionId, fieldKey } = currentEditing;

                                                    if (connectionId && fieldKey) {
                                                        saveCustomFieldValue(connectionId, fieldKey);
                                                    }
                                                }
                                            }}
                                            onBlur={() => {
                                                if (isEditing) {
                                                    const currentEditing = editingCustomFieldRef.current;
                                                    const { connectionId, fieldKey } = currentEditing;

                                                    if (connectionId && fieldKey) {
                                                        saveCustomFieldValue(connectionId, fieldKey);
                                                    } else {
                                                        cancelEditingCustomField();
                                                    }
                                                }
                                            }}
                                            onFocus={() => {
                                                if (!isEditing) {
                                                    // Create a synthetic event for startEditingCustomField
                                                    const syntheticEvent = {
                                                        stopPropagation: () => {},
                                                        preventDefault: () => {}
                                                    } as React.MouseEvent<HTMLElement>;
                                                    startEditingCustomField(syntheticEvent, row._id, field.key, value, field.type);
                                                }
                                            }}
                                            autoFocus={isEditing}
                                            placeholder={field.metadata?.placeholder || 'e.g. 702 123 4567'}
                                        />
                                    </PhoneFieldContainer>
                                ) : field.type === 'user' ? (
                                    <UserFieldContainer>
                                        {isEditing ? (
                                            <UserFieldInput
                                                type="text"
                                                value={userInputValue}
                                                onChange={(e) => {
                                                    const searchValue = e.target.value;
                                                    setUserInputValue(searchValue);
                                                    handleUserSearchChange(searchValue);
                                                }}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Escape') {
                                                        e.preventDefault();
                                                        cancelUserDropdownEditing();
                                                    }
                                                }}
                                                onBlur={() => {
                                                    // Delay to allow selection to complete
                                                    setTimeout(() => {
                                                        if (editingCustomFieldRef.current.fieldType === 'user') {
                                                            cancelUserDropdownEditing();
                                                        }
                                                    }, 150);
                                                }}
                                                autoFocus
                                                onClick={(e) => e.stopPropagation()}
                                                placeholder="Enter member name"
                                            />
                                        ) : (
                                            <UserFieldDisplay onClick={(e: React.MouseEvent<HTMLDivElement>) => {
                                                e.stopPropagation();
                                                startEditingCustomField(e, row._id, field.key, value, field.type);
                                            }}>
                                                {value && typeof value === 'object' && value.firstName && value.lastName ? (
                                                    <>
                                                        <ProfilePhoto
                                                            source={value.profilePicture}
                                                            capitalLetters={!value.profilePicture ?
                                                                `${value.firstName?.charAt(0)?.toUpperCase() || ''}${value.lastName?.charAt(0)?.toUpperCase() || ''}` :
                                                                undefined
                                                            }
                                                            height='24px'
                                                            width='24px'
                                                        />
                                                        <TooltipWrapper tooltipText={`${value.firstName} ${value.lastName}`}>
                                                            <UserFieldSelectedText>
                                                                {value.firstName} {value.lastName}
                                                            </UserFieldSelectedText>
                                                        </TooltipWrapper>
                                                    </>
                                                ) : (
                                                    <span style={{ color: '#8C96AD' }}>{field.metadata?.placeholder || 'Enter member name'}</span>
                                                )}
                                            </UserFieldDisplay>
                                        )}
                                        <UserFieldChevron>
                                            <SvgFragment type="arrowDown" />
                                        </UserFieldChevron>
                                    </UserFieldContainer>
                                ) : (
                                    <Text $ellipsis='0'>
                                        {formattedValue || <span style={{ color: styles.colors.black300 }}>{field.metadata?.placeholder || 'Click to edit'}</span>}
                                    </Text>
                                )}
                            </div>
                        )}
                    </div>
                );
            }
        }));

        // Add "Create New Field" column at the end
        const createFieldColumn: Column<IConnection> = {
            key: 'createNewField',
            name: '',
            width: 200,
            minWidth: 200,
            resizable: false,
            frozen: false,
            draggable: false,
            renderHeaderCell: () => (
                <CreateFieldHeaderCell
                    onClick={(e: React.MouseEvent<HTMLElement>) => {
                        e.stopPropagation();
                        setCreateCustomFieldModal(() => {
                            hideModal();
                        });
                    }}
                >
                    <CreateFieldIconWrapper>
                        <SvgFragment type='plus' />
                    </CreateFieldIconWrapper>
                    <CreateFieldLabel>CREATE NEW FIELD</CreateFieldLabel>
                </CreateFieldHeaderCell>
            ),
            renderCell: () => <div></div>
        };

        return [...baseColumns, ...customFieldColumns, createFieldColumn];
    }, [isSmall, workspaceIsStarter, currentUserId, workspaceUsers, customFieldDefinitions, canShowScanMutuals, canUseWarmIntroAi, dailyLimitReachedAi, showAutoTags, extensionInstalled, extensionVersion, isLoggedToLinkedin, columnWidths, selectedConnections.length, isAllSelected, pageSize, total, connections.length, resizableKeys, enrichingItems, sidebarEnrichingConnections, allOnPageSelected, loadingConversation, editingTrigger, setCreateCustomFieldModal, hideModal, updateCustomFieldValueHandler, userInputValue]);

    // Use columnsOrder from store
    const { columnsOrder = initialColumns.map(col => col.key) } = connectionsParameters;
    const [columns, setColumns] = useState<Column<IConnection>[]>(() => {
        const keyToCol = Object.fromEntries(initialColumns.map(col => [col.key, col]));
        const ordered = columnsOrder.map((key: string) => keyToCol[key]).filter(Boolean);
        const missing = initialColumns.filter(col => !columnsOrder.includes(col.key));
        // Ensure createNewField is always at the end
        const allCols = [...ordered, ...missing];
        const createFieldCol = allCols.find(col => col.key === 'createNewField');
        const withoutCreateField = allCols.filter(col => col.key !== 'createNewField');

        return createFieldCol ? [...withoutCreateField, createFieldCol] : allCols;
    });

    // Update columns when columnsOrder changes
    useEffect(() => {
        const keyToCol = Object.fromEntries(initialColumns.map(col => [col.key, col]));
        const ordered = columnsOrder.map((key: string) => keyToCol[key]).filter(Boolean);
        const missing = initialColumns.filter(col => !columnsOrder.includes(col.key));


        // Ensure createNewField is always at the end
        const allCols = [...ordered, ...missing];
        const createFieldCol = allCols.find(col => col.key === 'createNewField');
        const withoutCreateField = allCols.filter(col => col.key !== 'createNewField');

        setColumns(createFieldCol ? [...withoutCreateField, createFieldCol] : allCols);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columnsOrder]);

    // Update columns when initialColumns changes (e.g., new custom field added)
    const prevInitialColumnsRef = useRef(initialColumns);
    useEffect(() => {
        // Check if initialColumns actually changed (not just reference)
        const hasChanged = prevInitialColumnsRef.current.length !== initialColumns.length ||
            prevInitialColumnsRef.current.some((col, index) =>
                !initialColumns[index] || col.key !== initialColumns[index].key
            );

        if (hasChanged) {
            const keyToCol = Object.fromEntries(initialColumns.map(col => [col.key, col]));
            const ordered = columnsOrder.map((key: string) => keyToCol[key]).filter(Boolean);
            const missing = initialColumns.filter(col => !columnsOrder.includes(col.key));

            // Ensure createNewField is always at the end
            const allCols = [...ordered, ...missing];
            const createFieldCol = allCols.find(col => col.key === 'createNewField');
            const withoutCreateField = allCols.filter(col => col.key !== 'createNewField');

            setColumns(createFieldCol ? [...withoutCreateField, createFieldCol] : allCols);
            prevInitialColumnsRef.current = initialColumns;
        }
    }, [initialColumns, columnsOrder]);

    // Get all visible columns (including userInfo and createNewField which should always be visible)
    const visibleColumns = columns.filter((col: Column<IConnection>) => {
        // createNewField column should always be visible
        if (col.key === 'createNewField') return true;
        return visibilitySettings[col.key];
    });

    const rawTableWidth = tableRef?.current?.offsetWidth ?? 0;
    // Calculate widths: use saved width if available, else default (min or initial)
    const colWidths = visibleColumns.map((col: Column<IConnection>) => {
        const columnMinWidth = typeof col.minWidth === 'number' ? col.minWidth : minColumnWidth;
        if (typeof columnWidths[col.key] === 'number') return Math.max(columnMinWidth, columnWidths[col.key]);
        if (typeof col.width === 'number') return Math.max(columnMinWidth, col.width);
        return Math.max(columnMinWidth, rawTableWidth / visibleColumns.length);
    });

    // Stretch last column if needed
    const totalColsWidth = colWidths.reduce((sum: number, w: number) => sum + w, 0);
    let adjustedColWidths = [...colWidths];
    if (totalColsWidth < rawTableWidth && adjustedColWidths.length > 0) {
        adjustedColWidths[adjustedColWidths.length - 1] += rawTableWidth - totalColsWidth;
    }
    // Map widths back to columns, but use latest definitions from initialColumns
    const columnsWithWidths = columns.map((col: Column<IConnection>) => {
        const freshDef = initialColumns.find((c: Column<IConnection>) => c.key === col.key) || col;
        const visibleIdx = visibleColumns.findIndex((c: Column<IConnection>) => c.key === col.key);
        return visibleIdx !== -1 ? { ...freshDef, width: adjustedColWidths[visibleIdx] } : freshDef;
    });

    useEffect(() => {
        // Find the .rdg element inside the parent div after DataGrid renders
        if (tableRef.current) {
            const grid = tableRef.current.querySelector('.rdg') as HTMLDivElement;
            if (grid) {
                gridViewportRef.current = grid;
                if(resizableKeys.length > 0) {
                    const handleScroll = () => {
                        setResizableKeys([]);
                    };
                    grid.addEventListener('scroll', handleScroll);
                    return () => {
                        grid.removeEventListener('scroll', handleScroll);
                    };
                }
            }
        }
    }, [connections, columns]);

    const handleColumnsReorder = (sourceColumnKey: string, targetColumnKey: string) => {
        // Don't allow reordering of createNewField column
        if (sourceColumnKey === 'createNewField' || targetColumnKey === 'createNewField') return;
        const sourceIdx = columns.findIndex(col => col.key === sourceColumnKey);
        const targetIdx = columns.findIndex(col => col.key === targetColumnKey);
        if (sourceIdx === -1 || targetIdx === -1) return;
        const newColumns = [...columns];
        const [removed] = newColumns.splice(sourceIdx, 1);
        newColumns.splice(targetIdx, 0, removed);
        // Remove userInfo and createNewField from newColumns, keep them in their positions
        const withoutFixed = newColumns.filter(col => col.key !== 'userInfo' && col.key !== 'createNewField');
        const userInfoCol = columns.find(col => col.key === 'userInfo');
        const createFieldCol = columns.find(col => col.key === 'createNewField');
        // userInfo at start, others in middle, createNewField at end
        const finalColumns = [
            ...(userInfoCol ? [userInfoCol] : []),
            ...withoutFixed,
            ...(createFieldCol ? [createFieldCol] : [])
        ];
        const newOrder = finalColumns.map(col => col.key);
        localStorage.setItem('columns-order', JSON.stringify(newOrder));
        setConnectionsParametersHandler({ ...connectionsParameters, columnsOrder: newOrder });
    };

    const handleHeaderMouseEnter = (key: string) => {
        setResizableKeys(prev => prev.includes(key) ? prev : [...prev, key]);
    };

    return (
        <div
            ref={tableRef}
            style={{
                width: '100%',
                height: isSmall ? '50vh'
                    : !!connections.length
                    ? connections.length * 40 + 50 /* header height */ + 'px'
                    : 'calc(100vh - 500px)',
                overflow: 'auto',
                WebkitOverflowScrolling: 'touch',
                marginTop: '16px',
                backgroundColor: styles.colors.uiBackground
            }}
        >
            {workspace.initialSync && connectionsLoading ? (
                <Preloader />
            ) : (
                !!connections.length ? (
                    <StyledDataGrid
                        columns={columnsWithWidths.filter((item: any) => {
                            // createNewField column should always be visible
                            if (item?.key === 'createNewField') return true;
                            return visibilitySettings[item?.key];
                        })}
                        rows={connections}
                        rowKeyGetter={(row) => row._id}
                        onCellClick={(args) => {
                            // Check if the clicked cell is a custom field
                            const isCustomField = args.column.key && args.column.key.startsWith('customField_');

                            // If it's a custom field, don't open sidebar
                            if (isCustomField) {
                                return;
                            }

                            // Otherwise, open sidebar as usual
                            openSidebar(SidebarTypes.CONNECTION_ACTIONS, {
                                connection: args.row,
                            });
                        }}
                        onColumnResize={handleColumnResize}
                        className="rdg-light"
                        direction="ltr"
                        rowHeight={40}
                        defaultColumnOptions={{
                            draggable: true,
                          }}
                          onColumnsReorder={handleColumnsReorder}
                    />
                ) : (
                    <ConnectionTableEmptyPage props={
                        connectionsFilter?.quickActions === 'leadsearch' 
                            ? 'leadsearch'
                            : !extensionInstalled
                            ? (isChromeBasedBrowser() ? 'noExtensionInstalled' : 'unsupportedBrowser')
                            : (!workspace?.initialSync && moment().isBefore(moment(lastSyncAt).add(20, 'minutes'))) || syncConnectionsStarted
                            ? 'syncConnectionsInProgress'
                            : !(workspace?.initialSync || moment().isBefore(moment(lastSyncAt).add(20, 'minutes')) || syncConnectionsStarted)
                            ? 'workspaceNotSynced'
                            : 'default'
                        }
                    />
                )
            )}
            {actionDropdownState.connection && createPortal(
                <PortalDropdownWrapper
                    ref={portalRef}
                    style={{
                        position: 'fixed',
                        top: actionDropdownState.y,
                        left: actionDropdownState.x,
                        zIndex: 10050,
                        visibility: actionDropdownState.positioned ? 'visible' : 'hidden',
                        pointerEvents: actionDropdownState.positioned ? 'auto' : 'none'
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <ConnectionActionDropdown
                        connection={actionDropdownState.connection}
                        backgroundColor={'white'}
                        forceShow
                        onToggle={(isOpen) => { if (!isOpen) closeActionDropdownPortal() }}
                    />
                </PortalDropdownWrapper>,
                document.body
            )}
            {datePickerState.show && createPortal(
                <DatePickerPortalWrapper
                    ref={datePickerRef}
                    style={{
                        position: 'fixed',
                        top: datePickerState.y,
                        left: datePickerState.x,
                        zIndex: 10100
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <StyledCalendar
                        value={editingCustomFieldRef.current.value ? new Date(editingCustomFieldRef.current.value) : new Date()}
                        onChange={(date) => handleDateChange(date as Date)}
                        locale='en-US'
                        maxDate={new Date(2050, 11, 31)}
                        minDate={new Date(1900, 0, 1)}
                    />
                </DatePickerPortalWrapper>,
                document.body
            )}
            {richTextState.show && createPortal(
                <RichTextPortalWrapper
                    ref={richTextRef}
                    style={{
                        position: 'fixed',
                        top: richTextState.y,
                        left: richTextState.x,
                        zIndex: 10100
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <RichTextEditor
                        ref={richTextTextareaRef}
                        className="rich-text-editor"
                        value={richTextState.value}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setRichTextState(prev => ({ ...prev, value: e.target.value }))}
                        onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                                e.preventDefault();
                                saveRichTextValue();
                            } else if (e.key === 'Escape') {
                                e.preventDefault();
                                cancelRichTextEditing();
                            }
                        }}
                        placeholder="Enter rich text content"
                        rows={8}
                    />
                    <RichTextActions>
                        <RichTextToolbarLeft>
                            <EmojiPicker
                                onSelect={(emoji: string) => {
                                    const start = richTextTextareaRef.current?.selectionStart || 0;
                                    const newText = richTextState.value.substring(0, start) + emoji + richTextState.value.substring(start);
                                    setRichTextState(prev => ({ ...prev, value: newText }));
                                    setTimeout(() => {
                                        if (richTextTextareaRef.current) {
                                            richTextTextareaRef.current.focus();
                                            richTextTextareaRef.current.setSelectionRange(start + emoji.length, start + emoji.length);
                                        }
                                    }, 0);
                                }}
                            />

                            <ToolbarSvgWrapper onClick={richTextSparkleLoading ? undefined : () => setChooseTemplateModal((message: string) => {
                                const normalized = normalizeMessageForTextarea(message);
                                setRichTextState(prev => ({ ...prev, value: normalized }));
                                if (richTextTextareaRef.current) {
                                    richTextTextareaRef.current.focus();
                                    requestAnimationFrame(() => {
                                        if (!richTextTextareaRef.current) return;
                                        autosizeTextarea(richTextTextareaRef.current);
                                    });
                                }
                            })}>
                                <SvgFragment type='templates'/>
                            </ToolbarSvgWrapper>

                            {richTextSparkleLoading
                                ? <ToolbarSvgWrapper style={{ position: 'relative', bottom: '-5px' }}><LoadingSpinner height="15px" width="15px" /></ToolbarSvgWrapper>
                                : <Assistant content={richTextState.value} setContent={(value: string) => setRichTextState(prev => ({ ...prev, value }))} postCard onLoadingChange={setRichTextSparkleLoading}/>
                            }
                        </RichTextToolbarLeft>
                        <RichTextToolbarRight>
                            <Button
                                $type="grey"
                                $width="60px"
                                onClickHandler={cancelRichTextEditing}
                            >
                                Cancel
                            </Button>
                            <Button
                                $type="blue"
                                $width="60px"
                                onClickHandler={saveRichTextValue}
                            >
                                Save
                            </Button>
                        </RichTextToolbarRight>
                    </RichTextActions>
                </RichTextPortalWrapper>,
                document.body
            )}
            {userDropdownState.show && createPortal(
                <UserDropdownPortalWrapper
                    ref={userDropdownRef}
                    style={{
                        position: 'fixed',
                        top: userDropdownState.y,
                        left: userDropdownState.x,
                        zIndex: 10100
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <UserDropdownContainer>
                        <UserDropdownList>
                            {workspaceUsers
                                ?.filter((user: IWorkspaceUser) => {
                                    if (!userDropdownState.searchValue) return true;
                                    const fullName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
                                    const email = user.email?.toLowerCase() || '';
                                    const search = userDropdownState.searchValue.toLowerCase();
                                    return fullName.includes(search) || email.includes(search);
                                })
                                .map((user: IWorkspaceUser) => (
                                    <UserDropdownItem
                                        key={user._id}
                                        onClick={() => handleUserSelect(user, userDropdownState.connectionId!, userDropdownState.fieldKey!)}
                                    >
                                        <UserDropdownItemContent>
                                            <ProfilePhoto
                                                source={user.profilePicture}
                                                capitalLetters={!user.profilePicture ?
                                                    `${user.firstName?.charAt(0)?.toUpperCase() || ''}${user.lastName?.charAt(0)?.toUpperCase() || ''}` :
                                                    undefined
                                                }
                                            />
                                            <UserDropdownItemText>
                                                <span>{user.firstName || ''} {user.lastName || ''}</span>
                                                <span>{user.email}</span>
                                            </UserDropdownItemText>
                                        </UserDropdownItemContent>
                                    </UserDropdownItem>
                                ))
                            }
                        </UserDropdownList>
                    </UserDropdownContainer>
                </UserDropdownPortalWrapper>,
                document.body
            )}
            {infoBoxState.show && infoBoxState.connection && createPortal(
                <InfoBoxPortalWrapper
                    ref={infoBoxRef}
                    style={{
                        position: 'fixed',
                        top: infoBoxState.y,
                        left: infoBoxState.x,
                        zIndex: 10050,
                        visibility: infoBoxState.positioned ? 'visible' : 'hidden',
                        pointerEvents: infoBoxState.positioned ? 'auto' : 'none'
                    }}
                    onMouseEnter={() => {
                        isHoveringInfoBoxRef.current = true
                        cancelInfoBoxClose()
                        setInfoBoxState(prev => ({ ...prev, show: true }))
                    }}
                    onMouseLeave={() => {
                        isHoveringInfoBoxRef.current = false
                        scheduleInfoBoxClose(0)
                    }}
                >
                    <MembersPanelHeader>
                        <MembersSearchInput
                            value={memberSearch}
                            onChange={(e) => setMemberSearch(e.target.value)}
                            placeholder="Search member"
                            onClick={(e) => e.stopPropagation()}
                        />
                        <MembersCount>
                            {`Members (${(infoBoxState.connection?.users || []).length})`}
                        </MembersCount>
                    </MembersPanelHeader>
                    <MembersList>
                        {(infoBoxState.connection?.users || [])
                            .filter((u: any) => {
                                const normalize = (s: string) =>
                                    (s || '')
                                        .normalize('NFD')
                                        .replace(/[\u0300-\u036f]/g, '')
                                        .toLowerCase();

                                const q = normalize(memberSearch.trim());
                                if (!q) return true;
                                const full = normalize(`${u?.firstName || ''} ${u?.lastName || ''}`.trim());
                                return full.includes(q);
                            })
                            .map((u: any) => (
                                <UserInfo
                                    key={u._id}
                                    user={u}
                                    autotag={infoBoxState.connection?.autoTagsArray?.find((tag) => tag?._id === u._id)?.autoTags[0]}
                                    lastMessaged={infoBoxState.connection?.lastMessagedAtArray?.find((msg: any) => msg?._id === u._id)}
                                    removedFromLD={infoBoxState.connection?.removedFromLD}
                                />
                            ))}
                    </MembersList>
                </InfoBoxPortalWrapper>,
                document.body
            )}
        </div>
    )
}

const StyledMessaged = styled(Text)<{$sent: boolean}>`
    color: ${({ $sent }) => $sent ? styles.colors.black300 : styles.colors.green600 }
`
const StyledRow = styled(Row)`
    position: relative;
    & > *:not(:first-child) {
        margin-left: -2px;
    }
`
const StyledRowShared = styled(StyledRow)`
    cursor: pointer;
    & > *:nth-child(1) {
        margin-left: 0 !important;
        z-index: 1;
    }
    & > *:nth-child(2) {
        z-index: 2;
    }
    & > *:nth-child(3) {
        z-index: 3;
    }
    & > *:nth-child(4) {
        z-index: 4;
    }
`

const ConnectedMembersCell = styled(StyledRowShared)`
    position: relative;

    .add-member-button {
        opacity: 0;
        pointer-events: none;
    }

    &:hover .add-member-button {
        opacity: 1;
        pointer-events: auto;
    }
`

const AddMemberHoverButton = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    height: 24px;
    padding: 0 8px;
    border: none;
    border-radius: 6px;
    background: transparent;
    cursor: pointer;
    transition: opacity 0.15s ease, background-color 0.15s ease;

    span {
        font-family: NunitoSans;
        font-size: 14px;
        font-weight: 400;
        font-style: normal;
        line-height: 20px;
        letter-spacing: 0px;
        color: ${styles.colors.black600};
        white-space: nowrap;
    }

    &:hover {
        background: ${styles.colors.black100};
    }

    svg {
        width: 12px;
        height: 12px;
    }
`

const InfoBoxPortalWrapper = styled.div`
    width: 328px;
    height: 330px;
    background-color: ${styles.colors.white};
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    padding: 16px;
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    gap: 16px;
`

const MembersPanelHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    flex-shrink: 0;
`

const MembersSearchInput = styled.input`
    width: 100%;
    height: 40px;
    padding: 10px 12px;
    border: 1px solid ${styles.colors.black200};
    border-radius: 6px;
    outline: none;
    font-size: 14px;
    line-height: 20px;
    color: ${styles.colors.black600};
    background: ${styles.colors.white};

    &::placeholder {
        color: ${styles.colors.black300};
    }

    &:focus {
        border-color: ${styles.colors.primary600};
        box-shadow: 0 0 0 2px rgba(89, 100, 255, 0.12);
    }
`

const MembersCount = styled.div`
    font-family: NunitoSans;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    letter-spacing: 0px;
    color: ${styles.colors.black600};
`

const MembersList = styled.div`
    flex: 1;
    overflow-y: auto;
    padding-right: 6px;
`
const TextDescription = styled(Text)`
    color: ${styles.colors.black300};
    margin-bottom: 4px;
    font-size: 12px;
    line-height: 18px;
`
const InfoBoxColumn = styled(ColumnComponent)`
    margin-bottom: 12px;
`
const ProfilePhotoColumn = styled(ColumnComponent)`
    max-height: 32px;
`
const OverlappingPhoto = styled(ProfilePhoto)`
  margin-bottom: 4px;
  width: 24px;
  height: 24px;
`;

const MoreMembersPill = styled.div`
    margin-left: 6px;
    height: 24px;
    padding: 0 8px;
    border-radius: 12px;
    background: transparent;
    display: flex;
    align-items: center;
    font-family: NunitoSans;
    font-size: 12px;
    font-weight: 400;
    line-height: 18px;
    letter-spacing: 0px;
    color: ${styles.colors.black600};
    white-space: nowrap;
`

const EnrichActionContainer = styled.div`
    display: flex;
    width: fit-content;
    align-items: center;
    padding: 0px 6px 0px 4px;
    border-radius: 4px;
    border: 1px solid transparent;
`

const EnrichActionText = styled(ActionText)<{disabled?: boolean}>`
    span {    
        font-size: 14px;
    }
    div {
        margin-right: 4px;
    }
    ${({ disabled }) => disabled && `
        cursor: default;
        span, &:hover span, &:focus span{
            cursor: default;
            color: ${styles.colors.disabledState};
        }
        & svg { 
            path { 
                stroke: ${styles.colors.disabledState} !important; 
            } 
        }
    `}
`

const WarmIntroActionText = styled(ActionText)`
    && span {
        color: ${styles.colors.black300};
    }

    &&:hover span,
    &&:focus span {
        color: ${styles.colors.black600};
    }

    &&:hover .path,
    &&:focus .path,
    &&:hover svg path,
    &&:focus svg path {
        fill: ${styles.colors.primary600} !important;
        stroke: ${styles.colors.primary600} !important;
    }
`

const IntroPathsCellWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    min-width: 0;
    min-height: 100%;
    align-self: stretch;
    box-sizing: border-box;

    .refresh-paths {
        display: inline-flex;
        align-items: center;
        opacity: 0;
        max-width: 0;
        overflow: hidden;
        transition: opacity 0.15s ease, max-width 0.2s ease;
        white-space: nowrap;
    }

    &:hover .refresh-paths {
        opacity: 1;
        max-width: 200px;
    }
`

/** Same hover-reveal pattern as intro paths; on hover, value truncates with ellipsis so the action stays fully visible. */
const EnrichedValueCellWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    min-width: 0;
    min-height: 100%;
    align-self: stretch;
    box-sizing: border-box;

    .enriched-cell-value-text {
        font-family: NunitoSans, sans-serif;
        font-size: 14px;
        line-height: 20px;
        color: ${({ theme: { primaryColor } }) => primaryColor};
        min-width: 0;
        flex: 1 1 auto;
        overflow: visible;
        text-overflow: clip;
        white-space: normal;
        word-break: break-word;
    }

    &:hover .enriched-cell-value-text {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        word-break: normal;
    }

    /* No max-width transition: if it animates out while text is already white-space: normal, the cell stays narrow for ~200ms and the value flashes wrapped lines. */
    .refresh-paths {
        display: inline-flex;
        align-items: center;
        flex-shrink: 0;
        opacity: 0;
        max-width: 0;
        overflow: hidden;
        transition: opacity 0.12s ease;
        white-space: nowrap;
        pointer-events: none;
    }

    &:hover .refresh-paths {
        opacity: 1;
        max-width: 240px;
        pointer-events: auto;
    }
`

const RefreshPathsReveal = styled.div`
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
`

const RefreshPathsLabel = styled.span`
    font-family: 'Nunito Sans', sans-serif;
    font-weight: 700;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0;
    color: #333951;
    background: transparent;
`

const RefreshPathsButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    border: none;
    background: transparent;
    padding: 0;
    margin: 0;
    cursor: pointer;
    font: inherit;

    &:hover:not(:disabled) svg path {
        fill: ${styles.colors.primary600} !important;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    svg {
        width: 12px;
        height: 12px;
    }
`

const CellPhotoNameWrapper = styled(Row)`
    width: 400px;
`

const HeaderPhotoNameWrapper = styled(Row)`
    padding-left: 28px;
`
const TooltipComponent = styled.div`
    position: relative;
    &:hover div {
        display: block;
    }
    & div {
        display: none;
        position: absolute;
        top: -30px;
        z-index: 2;
        & span {
            border-radius: 3px;
            padding: 4px;
            font-size: 12px;
            background-color: ${({ theme: { septenaryColor } }) => septenaryColor};
            color: ${({ theme: { secondaryColor } }) => secondaryColor};
            white-space: nowrap;
        }
    }
`

const SortComponent = styled(Row)`
    height: 100%;
    width: 100%;
    justify-content: space-between;
    padding-right: 8px;
    span {
        cursor: pointer;
    }
    &:hover span {
        color: ${styles.colors.primary600};
    }
`

const PreloaderRow = styled(Row)`
    background: ${({ theme: { secondaryColor } }) => secondaryColor};
    align-items: center;
    padding: 10px 5px;
`

const StyledSVG = styled.svg<{ rotateArrow?: string, hoverEffect?: boolean }>`
    position: relative;
    transition: all .2s ease;
    ${({ rotateArrow }) => rotateArrow === 'za' ? 'transform: rotate(180deg);' : ''}
    ${props => props.hoverEffect && 'cursor: pointer;'}
    ${props => props.hoverEffect && 'fill: #8C96AD;'}
`

const ConnectionActionDropdownWrapper = styled.div`
    margin-left: auto;
    & .dropdown-menu {
        transform: translate3d(35px, 5px, 0px) !important;
    }
`
const HeaderCell = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
`

const WarmIntroHeaderTitle = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 8px;
`

// Match Header.tsx "BETA" pill styling
const BetaTag = styled.div`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    position: relative;
    right: -3px;
    height: 12px;
    width: 25px;
    font-size: 8px;
    background: ${styles.colors.primary600};
    border-radius: 3px;
    color: ${styles.colors.white};
`
const Resize = styled.div`
    width: 4px;
    height: 20px;
    background: #DAE0E8; 
    position: absolute;
    right: 5px;
    border-radius: 2px;
`
const PortalDropdownWrapper = styled.div`
    /* Hide the toggle/button inside the portalized dropdown */
    .dropdown-toggle, .dropdownWrapperReference { 
        display: none !important; 
    }
    /* Ensure the menu renders at the wrapper's coordinates (disable popper transform) */
    .actions-dropdown + div {
        transform: none !important;
        position: static !important;
        min-width: 170px;
    }
    .dropdown-menu {
        display: block;
    }
`

const CustomFieldInput = styled.input`
    width: 100%;
    padding: 4px 8px;
    border: 1px solid ${styles.colors.primary400};
    border-radius: 4px;
    font-size: 14px;
    line-height: 20px;
    outline: none;
    background-color: ${styles.colors.white};
    &:focus {
        border-color: ${styles.colors.primary600};
        box-shadow: 0 0 0 2px rgba(89, 100, 255, 0.1);
    }
`

const CustomFieldTextarea = styled.textarea`
    width: 100%;
    padding: 4px 8px;
    border: 1px solid ${styles.colors.primary400};
    border-radius: 4px;
    font-size: 14px;
    line-height: 20px;
    outline: none;
    background-color: ${styles.colors.white};
    resize: vertical;
    min-height: 60px;
    font-family: inherit;
    &:focus {
        border-color: ${styles.colors.primary600};
        box-shadow: 0 0 0 2px rgba(89, 100, 255, 0.1);
    }
`

const DatePickerPortalWrapper = styled.div`
    background: white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-radius: 8px;
    padding: 12px;
`

const StyledCalendar = styled(Calendar)`
    width: 240px;
    font-family: NunitoSans;
    font-size: 14px;
    font-weight: bold;
    color: ${styles.colors.black600};
    & abbr { text-decoration: none; }
    & button {
        border: none;
        background-color: transparent;
        &:disabled, &:disabled:hover { color: #b8bfcf; }
        &:not(:disabled) {
            color: #333951;
            cursor: pointer;
        }       
    }
    & .react-calendar__navigation {
        height: 20px;
        margin-bottom: 10px;
        display: flex;
    }
    & .react-calendar__navigation__label {
        font-weight: bold;
        color: ${styles.colors.black600};
        display: flex;
        justify-content: center;
    }
    & .react-calendar__navigation__arrow {
        line-height: 20px;
        font-size: 20px;
        font-weight: bold;
        color: #8c96ad;
    }
    & .react-calendar__month-view__weekdays {
        margin-bottom: 4px;
    }
    & .react-calendar__month-view__weekdays__weekday {
        text-align: center;
        font-size: 12px;
    }
    & .react-calendar__tile {
        height: 28px;
        width: 33px;
        margin-bottom: 2px;
        &:hover {
            color: ${styles.colors.primary600};
        }
    }
    & .react-calendar__tile--active {        
        background-color: #eef3ff;
        color: #333951 !important;
        &:hover { 
            background-color: #F6FBFF; 
        }
    }
    & .react-calendar__tile--now {
        background-color: ${styles.colors.black100};
        &:hover {
            background-color: ${styles.colors.black200};
        }
    }
    & .react-calendar__tile--rangeStart, & .react-calendar__tile--rangeEnd {
        border-radius: 6px;
        background-color: #5964ff;
        color: white !important;
        &:hover {
            background-color: #7a89fc;
            color: #333951;
        }
    }
`

const CreateFieldHeaderCell = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 4px;
    cursor: pointer;
    padding: 8px;
    transition: background-color 0.2s ease;
    &:hover {
        background-color: ${styles.colors.black100};
    }
`

const CreateFieldIconWrapper = styled.div`
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    svg {
        width: 14px;
        height: 14px;
    }
    path {
        fill: ${styles.colors.black300};
    }
`

const CreateFieldLabel = styled(Text)`
    color: ${styles.colors.black400};
    font-weight: 400;
    font-style: Regular;
    font-size: 11px;
    line-height: 16px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
`

const RichTextPortalWrapper = styled.div`
    background: white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-radius: 8px;
    padding: 10px;
    border: 1px solid #DAE0E8;
    width: 368px;
    height: 262px;
    display: flex;
    flex-direction: column;
    gap: 10px;
`

const RichTextEditor = styled.textarea`
    width: 100%;
    padding: 16px;
    border: 2px solid #DAE0E8;
    border-radius: 8px;
    font-size: 14px;
    line-height: 20px;
    outline: none;
    background-color: ${styles.colors.white};
    resize: none;
    flex: 1;
    font-family: inherit;
    &:focus {
        border-color: ${styles.colors.primary600};
        box-shadow: 0 0 0 2px rgba(89, 100, 255, 0.1);
    }
    &::placeholder {
        color: ${styles.colors.black300};
    }
`

const RichTextActions = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
`

const RichTextToolbarLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding-left: 4px;
   
`

const RichTextToolbarRight = styled.div`
    display: flex;
    gap: 8px;
`

const ToolbarSvgWrapper = styled.div`
    cursor: pointer;
    & svg {
        width: 20px;
        height: 20px;
        g {
            transform: translate(0px, 0px) scale(1.2);
            transform-origin: center;
            transform-box: fill-box;
        }
       
    }
    &:hover svg path {
        fill: ${styles.colors.primary600};
    }
`

const UrlFieldContainer = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
    gap: 8px;
`

const UrlText = styled.div`
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: ${styles.colors.black300};
    font-size: 14px;
    font-weight: 400;
    font-style: Regular;
    line-height: 20px;
`

const UrlActions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
`

const UrlActionButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border: none;
    background: none;
    cursor: pointer;
    padding: 0;
    
    &:hover svg path {
        stroke: ${styles.colors.primary600};
    }
`

const PhoneFieldContainer = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    height: 30px;
    border: 1px solid ${styles.colors.black200};
    border-radius: 6px;
    background-color: ${styles.colors.white};
    overflow: hidden;
    box-sizing: border-box;
    
    &:hover {
        border-color: ${styles.colors.primary600};
    }
    
    &:focus-within {
        border-color: ${styles.colors.primary600};
        box-shadow: 0 0 0 2px rgba(89, 100, 255, 0.1);
    }
`


const EmailFieldContainer = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    height: 100%;
    gap: 8px;
`

const EmailText = styled.div`
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: ${styles.colors.black300};
    font-size: 14px;
    font-weight: 400;
    font-style: Regular;
    line-height: 20px;
`

const EmailActions = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
`

const EmailActionButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border: none;
    background: none;
    cursor: pointer;
    padding: 0;
    
    svg path {
        transition: fill 0.2s ease;
    }
    
    &:hover svg path {
        stroke: ${styles.colors.primary600};
    }
`

const NumberFieldContainer = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
    height: 30px;
    border: 1px solid ${styles.colors.black200};
    border-radius: 6px;
    background-color: ${styles.colors.white};
    overflow: hidden;
    box-sizing: border-box;
    
    &:hover {
        border-color: ${styles.colors.primary600};
    }
    
    &:focus-within {
        border-color: ${styles.colors.primary600};
        box-shadow: 0 0 0 2px rgba(89, 100, 255, 0.1);
    }
`

const NumberInput = styled.input`
    flex: 1;
    height: 100%;
    padding: 0 12px;
    border: none;
    font-size: 14px;
    line-height: 20px;
    color: ${styles.colors.black600};
    background-color: transparent;
    outline: none;
    box-sizing: border-box;
    
    &::placeholder {
        color: ${styles.colors.black400};
    }
    
    /* Hide default number input spinners - Chrome, Safari, Edge, Opera */
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none !important;
        appearance: none !important;
        margin: 0 !important;
        display: none !important;
    }
    
    /* Firefox */
    &[type=number] {
        -moz-appearance: textfield !important;
        appearance: textfield !important;
    }
    
    /* Additional override for all browsers */
    -webkit-appearance: none;
    -moz-appearance: textfield;
    appearance: none;
`

const NumberDisplay = styled.div`
    flex: 1;
    height: 100%;
    padding: 0 12px;
    display: flex;
    align-items: center;
    font-size: 14px;
    line-height: 20px;
    color: ${styles.colors.black600};
    cursor: pointer;
    box-sizing: border-box;
`

const NumberSteppers = styled.div`
    display: flex;
    flex-direction: column;
    width: 32px;
    flex-shrink: 0;
    gap: 0;
    & svg {
        width: 17px !important;
        height: 17px !important;
    }
`

const NumberStepperButton = styled.button<{ $isEditing?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    border: none;
    background: none;
    cursor: ${({ $isEditing }) => $isEditing ? 'pointer' : 'default'};
    padding: 0;
    height: 7px;
    pointer-events: ${({ $isEditing }) => $isEditing ? 'auto' : 'none'};
    
    &:hover {
        background-color: ${({ $isEditing }) => $isEditing ? styles.colors.black100 : 'transparent'};
    }
    
    &:active {
        background-color: ${({ $isEditing }) => $isEditing ? styles.colors.black200 : 'transparent'};
    }
    
    svg {
        width: 12px;
        height: 12px;
        
        path {
            fill: ${styles.colors.black300};
        }
    }
    
    &:hover svg path {
        fill: ${({ $isEditing }) => $isEditing ? styles.colors.primary600 : styles.colors.black400};
    }
`

const UserDropdownPortalWrapper = styled.div`
    background: white;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    border-radius: 8px;
    border: 1px solid #DAE0E8;
    width: 260px;
    max-height: 400px;
    overflow: hidden;
`

const UserDropdownContainer = styled.div`
    width: 100%;
    height: 100%;
`

const UserDropdownList = styled.div`
    max-height: 400px;
    padding: 8px 0px;
    overflow-y: auto;
`

const UserDropdownItem = styled.div`
    padding: 8px 12px;
    cursor: pointer;
    border-bottom: 1px solid ${styles.colors.black100};
    
    &:hover {
        background-color: ${styles.colors.black100};
    }
    
    &:last-child {
        border-bottom: none;
    }
`

const UserDropdownItemContent = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`

const UserDropdownItemText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;
    
    span:first-child {
        font-size: 14px;
        font-weight: 500;
        color: ${styles.colors.black600};
        line-height: 1.2;
    }
    
    span:last-child {
        font-size: 12px;
        color: ${styles.colors.black400};
        line-height: 1.2;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }
`

export default ConnectionTable